import {
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
  type ReactNode,
  type Ref,
} from 'react';
import { useSearchParams } from 'react-router-dom';
// The one calendar-date check in the app, shared with the catalog forms and
// with `/projects`: shape AND existence, so '2026-02-30' is caught here rather
// than as a 422 naming the query parameter. Never `new Date(value)`.
import { isRealDate } from '@/components/catalog/form-utils';
import {
  PencilIcon,
  PlusIcon,
  SearchIcon,
  TrashIcon,
} from '@/components/catalog/icons';
/*
 * ⚠️ **`ImportedFromXeroBadge` was imported here until 2026-09-02**, mounted
 * twice — inside the Status cell and inside the card's status group. It went
 * when "From Xero" became a COLUMN of its own, because the owner asked for
 * *"a new column with yes no"* and two renderings of one fact on one row is the
 * drift this codebase keeps recording.
 *
 * ⛔ **THE COMPONENT IS NOT DEAD AND MUST NOT BE DELETED.** It has four mount
 * points and this page held two of them; `/quotes`' list row and BOTH detail
 * pages still render it, and its own header says so. This import going is a
 * statement about THIS list, not about the badge.
 */
// The cell, shared with `/quotes` — the same question about the same column on
// two line tables, so it must not be rendered twice.
import { DownloadPdfButton } from '@/components/documents/DownloadPdfButton';
import { LineProjectStatusCell } from '@/components/documents/LineProjectStatusCell';
import { useRaiseCreditNote } from '@/components/credit-notes/use-raise-credit-note';
import { RaiseCreditNoteButton } from '@/components/credit-notes/RaiseCreditNoteButton';
import { useInvoiceStatusFlags } from '@/components/credit-notes/use-invoice-status-flags';
import { InvoiceFormModal } from '@/components/invoices/InvoiceFormModal';
import { InvoiceIcon, InvoiceMoney, StatusBadge } from '@/components/invoices/InvoiceBits';
import { KanbanBoard } from '@/components/kanban/KanbanBoard';
/*
 * ⚠️ **`XeroRowPushControl`, `XeroPushReportBanner` and the `XeroRowPushReport`
 * type were imported here until 2026-08-31**, for the Xero column removed that
 * day — see the note where that column's `<TH>` was. Nothing on this page pushes
 * to Xero any more, so there is no outcome to report and no banner to raise.
 *
 * ⚠️ The component still exists and is still exported. It is NOT dead by
 * accident: `XeroPushReportBanner` from the same module is imported by
 * `InvoiceFormModal`, which reports the push a SAVE performs. Do not delete
 * `XeroRowPushControl.tsx` on the strength of this page no longer importing it.
 */
import { isOverdue } from '@/lib/invoice-utils';
import { ApiError, api } from '@/lib/api';
/*
 * ⚠️ **THE GATE IS IMPORTED, NOT DECLARED — and it is imported by
 * `InvoiceDetailPage` too.** One copy, because what would drift is *which
 * invoices may be credited*, on a control that hands money back. Read its doc
 * before changing the rule: it enforces the owner's rule — any invoice that has
 * been ISSUED, and not a cancelled one — as `isAwaitingPayment || isPaid`.
 */
import { creditNoteGateOf, type CreditNoteGate } from '@/lib/credit-notes';
import { cx } from '@/lib/cx';
/* ✅ **`canDownloadPdf` IS NO LONGER IMPORTED HERE, and the function is very
   much alive** — `DownloadPdfButton` gates itself on it, which is why nothing
   on this page has to. *SUPERSEDED, with its own note: "The visibility test
   only — the control words its own refusals. Importing this rather than
   `linkOf` keeps `@/lib/xero` out of this page": `import { canDownloadPdf }
   from '@/lib/document-pdf';`.*

   Both of its call sites were conditions asking "does this row have ANY
   control", and the credit-note control — which renders on every row, enabled
   or disabled — made both of them permanently true. ⚠️ The name survives in
   two comments recording exactly that, so **a grep finds it and proves
   nothing**; this is the "grep the surface, never the name" trap arriving on
   an import. */
import { preferencesStore } from '@/lib/preferences-store';
import { useSavedFilterApply, useSavedFilters } from '@/lib/use-saved-filters';
import {
  formatDateOnly,
  formatDateTime,
  documentDisplayTitle,
  formatList,
} from '@/lib/format';
/*
 * The two words, the column key, the wire vocabulary and the option list for
 * "From Xero", shared with `/quotes`.
 *
 * ⚠️ **FOUR names, and `get`/`format` are the pair that has to be the right way
 * round.** `importedFromXeroValue` is the column's `get` and speaks the WIRE's
 * `'true'`/`'false'`; `importedFromXeroFormat` turns that back into the word for
 * the popover; `importedFromXeroLabel` takes the raw nullable timestamp and is
 * what the CELL renders. `useTableFilters` puts a chosen option's value into the
 * query param verbatim, so a `get` that emitted `'Yes'` would send
 * `?importedFromXero=Yes` — a 422 that empties the table rather than a bad
 * filter. That module's header carries the whole reasoning; read it before
 * touching any of the three.
 */
import {
  IMPORTED_FROM_XERO_COLUMN,
  IMPORTED_FROM_XERO_FILTER_OPTIONS,
  importedFromXeroFormat,
  importedFromXeroLabel,
  importedFromXeroValue,
} from '@/lib/imported-from-xero';
/*
 * "Sent to client" — owner decision 2026-10-09. Same split as From Xero above:
 * `sentToClientValue` is the column's `get` and speaks the WIRE (`'true'` /
 * `'false'`), `sentToClientFormat` turns that back into the word, and the cell
 * renders from the raw fields through `sentCellText` below. ⛔ Never worded as
 * delivered — Xero records an accepted request, not an arrival.
 */
import {
  SENT_TO_CLIENT_COLUMN,
  SENT_TO_CLIENT_FILTER_OPTIONS,
  SENT_TO_CLIENT_LABEL,
  formatSentToClientDate,
  sentToClientFormat,
  sentToClientTitle,
  sentToClientValue,
} from '@/lib/sent-to-client';
// The four words and the option list, shared with `/quotes` — see that module's
// header for why this question has four answers and not two.
import {
  LINE_PROJECT_FILTER_OPTIONS,
  lineProjectStatusLabel,
} from '@/lib/line-project-status';
import { denialReason } from '@/lib/permissions';
import type {
  Invoice,
  InvoiceListParams,
  InvoiceListSort,
  InvoiceStatus,
  KanbanQueryParams,
  LineProjectStatus,
  Paginated,
} from '@/lib/types';
import { useAuth } from '@/lib/use-auth';
import { fetchAllPages, useShowAll } from '@/lib/use-show-all';
/*
 * The Account lead filter's DEFAULT — the membership test, the arrival states
 * and the URL write, in one module shared with `/deals` and `/quotes`. Read its
 * header before changing anything about how this page arrives; in particular, it
 * explains why a link naming ANY filter suppresses the seed.
 */
import { useDefaultAccountLeadFilter } from '@/lib/use-default-account-lead';
/*
 * ⚠️ **Read ONLY as the evidence for the Account lead default** — this page
 * renders no staff picker and no staff funnel, and must not grow one on the
 * strength of this call. `byGroup.account_lead` answers "may this person be
 * named as an account lead", which is the question the default turns on.
 */
import { useUserOptions } from '@/lib/use-user-options';
/*
 * ⚠️ **`selectableStatuses` IS DELIBERATELY NOT IMPORTED HERE ANY MORE.** It
 * narrows to ACTIVE rows, which is right for a create picker and wrong for
 * every control on this page — see `statusOptions`, which builds the filter
 * vocabulary itself for exactly that reason.
 */
import {
  currencyOptions,
  useCurrencyOptions,
  useInvoiceStatusOptions,
} from '@/lib/use-invoice-options';
/*
 * The house project picker, shared with the four consumer forms.
 *
 * ⚠️ **`projectOptions()` RATHER THAN A LOCAL `withSelected(...)`, and that is a
 * deliberate choice against the two existing project filters, not an oversight.**
 * `/accounts` and `/client-deliverables` both build their project picker from a
 * module-local `withSelected` + `FilterField` pair — but so do `/projects` and
 * `/invoice-tracker`: measured 2026-09-02, that is **FOUR byte-identical copies
 * of `withSelected` and four of `FilterField`**, none of them a `ui/` primitive
 * and none importable. Matching them here does not adopt a shared spelling; it
 * writes a FIFTH copy of both into a page that has neither.
 *
 * `projectOptions()` is the real shared function, written for exactly this job,
 * and it is already how this page builds its other two pickers — `currencyOptions`
 * and `selectableStatuses` from `use-invoice-options.ts`, rendered through the
 * shared `Combobox`. So this follows THIS file's convention and the app's one
 * genuine module, rather than a duplication the repo already lists as an open item.
 *
 * ⚠️ The two also word an off-list value differently and `projectOptions()` is
 * the more precise here: `withSelected` renders `Project #12` hinted "Not in the
 * list below", where `projectOptions()` renders the name plus **"(no longer
 * offered)"** — which is what a RETIRED project actually is, and it withholds
 * that suffix when the list is empty, because an empty list is "still loading"
 * or "failed", never "every project was retired".
 */
import { projectOptions, useProjectOptions } from '@/lib/use-project-options';
/*
 * Per-table column layout — order, hidden set, and the two frozen edges — stored
 * AGAINST THE ACCOUNT under `preferences.columns.invoices`.
 *
 * ⚠️ **IT IS A SEPARATE STORE FROM THE SAVED FILTERS ABOVE, AND MUST STAY ONE.**
 * `SAVED_FILTER_KEYS` feeds the URL parse loops and `useSavedFilters`' whole-set
 * replace, which treats every key it finds in its own branch as one more filter
 * to delete — a layout parked in there would be wiped by the next filter save.
 * Column keys therefore never appear in that array. The hook's own header
 * carries the long version, including why this is not in the URL: a link
 * describes WHICH ROWS, and imposing the sender's hidden columns on the
 * recipient is not what a shared link is for.
 */
import { useColumnLayout, useFrozenOffsets } from '@/lib/use-column-layout';
import { useTableFilters, type ColumnSpec } from '@/lib/use-table-filters';
import { useRememberedState } from '@/lib/filter-memory/use-filter-memory';
import {
  Badge,
  Button,
  /*
   * ✅ **`ColumnFilter` IS NO LONGER IMPORTED HERE.** *SUPERSEDED: "`ColumnFilter`
   * IS THE FUNNEL ITSELF, and it is imported here only for
   * `FrozenFilterableTH`" — the local header cell that re-spelled `FilterableTH`
   * so a frozen column could be handed its measured offset.* `FilterableTH`
   * forwards `frozen`/`frozenOffset` now, so there is one header path again and
   * this page renders no funnel of its own.
   */
  ColumnManager,
  Combobox,
  ConfirmDialog,
  EmptyState,
  Field,
  FilterableTH,
  FilterSheet,
  Input,
  /*
   * The Projects filter, which takes several ids. `Combobox` above still serves
   * Status and Currency — both single-valued on this endpoint, where a
   * comma-separated `?statusId=1,2` is a 422 — so the two live side by side
   * here and neither is a replacement for the other.
   */
  MultiCombobox,
  ListPager,
  PageHeader,
  Panel,
  PanelHeader,
  Skeleton,
  Table,
  TableWrap,
  TBody,
  TD,
  THead,
  TR,
  type ColumnFilterOption,
  type ColumnFilterProps,
  type FilterableTHProps,
  type FrozenSide,
} from '@/components/ui';
import { Notice } from '@/components/ui/Notice';
import { TextLink } from '@/components/ui/TextLink';
import { ViewToggle as SharedViewToggle } from '@/components/ui/ViewToggle';

/** `perPage` on /invoices is 1..100 and defaults to 20. */
const PER_PAGE = 20;
const SKELETON_ROWS = 6;

/*
 * `cardsPerColumn` is 1..100, default 20. Left at the default: there is no
 * per-column "load more" endpoint, so raising it only makes the first paint
 * heavier — the column headers count every match either way, so a column that
 * is showing 20 of 63 still says 63.
 */
const BOARD_CARDS_PER_COLUMN = 20;

/*
 * Table | Board, and the choice lives in the URL so either view is a real link
 * you can send someone.
 *
 * THE TABLE IS THE DEFAULT, because the PER-RECORD ACTIONS LIVE THERE. Tasks and
 * notes, Edit and Delete are row actions with no board equivalent, so opening on
 * the board puts every per-record action behind a toggle. A view that cannot act
 * on a record is not the one to land on.
 *
 * So an absent `view` key means the table, and only the exact value `board` asks
 * for the board. Anything else means the table — a pasted `?view=kanban` is a
 * typo, not a 404. `?view=table` is still read as the table, so links written
 * while the board was the default still land on it.
 */
type InvoicesView = 'table' | 'board';

const VIEW_OPTIONS: readonly { value: InvoicesView; label: string }[] = [
  { value: 'table', label: 'Table' },
  { value: 'board', label: 'Board' },
];

/*
 * ── What the board honours ──────────────────────────────────────────────────
 *
 * /kanban/invoices takes every filter this page can set BAR ONE: the four common
 * ones (`search`, `ownerId`, `companyId`, `contactId`), `accountLeadId`, plus
 * `statusId`, `dealId`, `quoteId`, `currency`, `dueDateAfter` and
 * `dueDateBefore`. Those filters narrow the COUNTS and TOTALS in the column
 * headers, not merely the cards on show, so a header never describes a different
 * set to the cards beneath it. The one exception is `projectId`, which the board
 * does not declare — see `boardParams` and the notice rendered beside its badge.
 *
 * The board schema is `.strict()` and PER BOARD: an unknown key, or one that
 * belongs to another board (`stageId`, `dealTypeId`, `leadSourceId`,
 * `validTillAfter`), is a 422 rather than the silent no-op it used to be. That
 * is why `boardParams` is assembled key by key from the list above and never by
 * spreading the page's filter state, which is shaped for the list endpoint and
 * matches this one only by coincidence.
 *
 * `search` means the same thing in both views: the list endpoint matches
 * `subject` and nothing else, and the board's search matches the card title,
 * which for an invoice IS `subject`. (The board's LIKE is unescaped, so a
 * literal `%` behaves as a wildcard there — that is the hook's business, not
 * this page's copy.)
 *
 * The board's columns are STATUSES, not stages: its `columnKey` is `statusId`,
 * its filter key is `statusId` too — `stageId` here is a 422 — and the move
 * endpoint is `PATCH /invoices/:id/status`. Nothing on this page says "stage".
 */

/*
 * ── Ordering ────────────────────────────────────────────────────────────────
 *
 * ⚠️ **SUPERSEDED 2026-09-02, and it was wrong TWICE OVER: "Ordering is fixed at
 * `id DESC` server-side — there is NO sort parameter on this resource, and
 * unknown query keys are stripped in silence, so a sortable header would
 * highlight and the rows would not move."**
 *
 * `sort` and `order` shipped on 2026-08-31 ({@link InvoiceListSort}), and this
 * query schema is `.strict()` — an unknown key is a **422 naming it**, never a
 * silent strip. So both halves of the old sentence were false, and each pointed
 * the reader somewhere different: the first said the feature could not exist,
 * the second said a wrong key would be harmless.
 *
 * ⚠️ **It rotted unnoticed because it was a NEGATIVE claim**, which is this
 * codebase's documented failure mode: nothing fails when a `sort` parameter
 * starts existing in another repo — no render breaks, no type complains, no
 * request 422s — so a positive claim is killed by its first counter-example
 * while a negative one has to be re-measured on purpose. Nobody did, for two
 * days, while the page kept working perfectly.
 *
 * What is true now:
 *
 * - `id DESC` is the endpoint's DEFAULT, and it is asked for by sending
 *   NOTHING. ⚠️ **Never send `sort=id&order=desc`** — it is the identical
 *   ordering spelled as an instruction, so the day the default moves this page
 *   would silently pin the old one.
 * - **FOUR sortable headers: Status (`sort=status`), Bill date
 *   (`sort=billDate`), Projects (`sort=lineProjectStatus`) and From Xero
 *   (`sort=importedFromXero`).** *SUPERSEDED 2026-09-02, twice in one day:
 *   "The only sortable header here is Bill date", then "TWO sortable headers".*
 *   `subject` is a real member too, but the Invoice column renders the subject
 *   AND the Xero number, so an arrow on it would claim to order by a column the
 *   reader can see is only half of what is in the cell. Out of scope
 *   deliberately — that is an owner decision, not an omission.
 * - ⚠️ **`status` ORDERS BY THE STATUS'S OWN `sort_order`, NOT
 *   ALPHABETICALLY**, so ascending walks the pipeline rather than the alphabet
 *   — Draft before Authorised before Paid, whatever those rows happen to be
 *   called. **And `status_id` is NULLABLE with no server-side sentinel**, so
 *   `order=asc` OPENS ON THE INVOICES WITH NO STATUS AT ALL, exactly as
 *   `?sort=startDate&order=asc` opens on undated projects. Nothing on this page
 *   says otherwise, and nothing may: a sentinel would not move an unstatused
 *   invoice out of the way, it would file it under a status it does not hold.
 * - ⚠️ **`importedFromXero` sorts on a BOOLEAN, and which end ascending starts
 *   at is DELIBERATELY NOT ASSERTED HERE.** It has not been driven against a
 *   live response from this session, and a guess written into a comment is the
 *   artefact the next reader trusts instead of measuring. The header carries no
 *   copy about it either way.
 * - **`lineProjectStatus` orders by a four-step COMPLETENESS RANK**, not
 *   alphabetically: `no_lines`, `none`, `some`, `all`, least complete first. So
 *   ascending opens on the invoices with the most projects still to add. It is
 *   also the page's only SERVER-scoped funnel — the filter and the sort are one
 *   header, and both act on the whole list rather than on the loaded page.
 * - 🔴 **There is NO `invoiceNumber` sort and there cannot be**: `d_invoices`
 *   has no such column. `FilterableTH` claims `aria-sort` only where the API
 *   can really sort, so that header stays plain rather than promising a control
 *   that will never exist.
 *
 * ── Filters in the URL ──────────────────────────────────────────────────────
 *
 * The id filters and the two bill-date bounds live in the URL so a filtered view
 * survives a refresh and can be linked to — `/invoices?companyId=22` off a
 * company record. Every write is `replace: true`: changing a filter is not a
 * navigation, and stacking one history entry per filter press makes Back mean
 * "undo one filter".
 *
 * `search` stays in local state: it is debounced per keystroke, and a half-typed
 * term is not worth a URL write.
 */
const ID_FILTERS = [
  /*
   * `statusId` moved to `ID_LIST_FILTERS` on 2026-10-28 (owner: every filter
   * takes several values). The ids below stay single: each is arrived at by
   * link and shown as a badge, with no control here that could pick a second.
   */
  'contactId',
  'companyId',
  'dealId',
  'quoteId',
  /*
   * ⚠️ **`ownerId` was here and went on 2026-08-31**, with the Owner column,
   * the "All owners" control and the rest of the owner surface. `/invoices`
   * still accepts it; this app stopped asking. Nothing ever wrote `owner_id` on
   * an invoice — the server puts the acting user in `created_by_id` and leaves
   * owner null — so it matched nothing and an empty list under it was
   * indistinguishable from having no invoices. Measured 2026-08-31: **0 of 33
   * invoices carry an owner.**
   *
   * `accountLeadId` is the replacement and a DIFFERENT person, not a rename. It
   * matches the RESOLVED lead (the invoice's own where it has one, otherwise
   * its company's) and is a SINGLE id — comma-separated values are
   * companies-only, so `?accountLeadId=1,2` is a 422 here.
   *
   * It scopes what is SHOWN and nothing else. No list endpoint narrows itself to
   * the caller, so this is a view, never access control.
   */
  'accountLeadId',
  /*
   * The saleable run an invoice was raised for. A real filter on
   * `GET /invoices`, arrived at by link from `/projects/:id` rather than from a
   * control here — like `companyId`, `dealId` and `quoteId`, which is why it
   * belongs in this list and gets a badge rather than a select.
   *
   * ⚠️ IT IS NOT A BOARD KEY. The kanban query schema is `.strict()` and built
   * PER BOARD from the backend's `kanban.registry.ts`; the invoices board
   * declares `companyId`, `contactId`, `accountLeadId`, `statusId`, `dealId`,
   * `quoteId`, `currency` and the two `dueDate` bounds — plus `ownerId`, which
   * this app no longer sends anywhere — and NOTHING
   * ELSE — the per-board table in the backend's `docs/api/kanban.md`, not
   * inferred. Sending `projectId` to `/invoices/kanban` would 422 the whole
   * board rather than being ignored, so it is deliberately absent from
   * `boardParams` and the board says out loud that it is not narrowed by it.
   */
  'projectId',
] as const;
type IdFilter = (typeof ID_FILTERS)[number];

/**
 * The id filters that take SEVERAL ids — a comma-separated list on the wire,
 * `?lineProjectId=4,9`, where several ids mean **OR**.
 *
 * 🔴 **A SEPARATE FAMILY FROM `ID_FILTERS` BECAUSE THE VALUE SHAPE IS
 * DIFFERENT, NOT BECAUSE THE MECHANISM IS.** Everything else about it is
 * identical to the list above and to `DATE_FILTERS` below: parsed out of the
 * query string in the same memo, written back through the same `setFilter`,
 * saved under the same `SAVED_FILTER_KEYS`, counted in the same
 * `activeFilterCount`. `filters[key]` is a `number[]` here rather than a
 * `number | null`, which is the whole reason the loops cannot be shared —
 * `parseId` returns one integer and `params[key] = value` assigns one.
 *
 * ⚠️ **AN EMPTY SELECTION MEANS NO FILTER AND THE KEY IS OMITTED ENTIRELY.**
 * `?lineProjectId=` is a **422** on this `.strict()` schema, not "no filter" —
 * the same rule every other key on this page follows, and the reason
 * `setFilter` is handed `''` rather than an empty CSV when the last chip goes.
 *
 * ⚠️ **Membership is a property of the KEY, checked against the server's
 * schema, never a shape this page may assume.** *SUPERSEDED 2026-10-28:
 * "`lineProjectId` is the ONLY member … `?statusId=1,2` is still a 422".*
 * `statusId` joined it when the backend widened its id filters to CSV.
 * `IdListFilter` in `types.ts` carries the long version.
 */
const ID_LIST_FILTERS = [
  /*
   * The status filter, written by the "All statuses" multiselect and the
   * Status funnel alike (see `bridgeStatusToUrl`). `?statusId=3,5` ORs on the
   * list and on the invoices board since 2026-10-28; a bookmarked single id
   * still parses as a one-member list.
   */
  'statusId',
  /*
   * 🔴 **ONE PREFIX APART FROM `projectId` ABOVE, AND A DIFFERENT QUESTION**:
   * that one asks whether the invoice's HEADER names the project, this asks
   * whether any of its LINE ITEMS does. **`lineProjectId` is the one with a
   * control**, and the one that matches anything.
   *
   * ⚠️ **The measurements and the reasons live on `InvoiceListParams` in
   * `types.ts`, at both keys — read them there, not here.** They are not
   * repeated in this file on purpose: a count copied into a second place is a
   * count that goes stale in one of them, and the type is the copy every
   * consumer of this endpoint already has to read.
   *
   * Three consequences that belong to THIS page rather than to the contract:
   *
   * ⚠️ **They are not a fallback pair and must never be OR-ed together.** The
   * server treats them as independent clauses, so sending both ANDs them — a
   * legitimate request, not a mistake to collapse into one. That stays true
   * now that this one takes several ids: the ids OR among THEMSELVES, and the
   * two KEYS still AND with each other.
   *
   * ⚠️ **`lineProjectId` is "bills this project", never "bills ONLY this
   * project".** An invoice with one line on project 7 and three on project 9
   * matches both `lineProjectId=7` and `lineProjectId=9`, so this filter can
   * never be read as a partition of the list. Selecting both is still not a
   * partition — it is the union, and the same invoice answers to it once.
   *
   * ⚠️ **Several ids never narrow the list; they only ever widen it.** An
   * invoice matches when ANY of its lines names ANY selected project, so
   * adding a chip can only ever add rows. A reader ticking a second project
   * expecting "invoices billing both" gets "invoices billing either", and
   * nothing on screen distinguishes the two — the API offers no AND form of
   * this key, so there is nothing to build towards and nothing to promise.
   */
  'lineProjectId',
] as const;
/*
 * ⚠️ **`…Key`, breaking the `IdFilter` / `DateFilter` naming beside it on
 * purpose.** `IdListFilter` is already an EXPORTED type in `types.ts` and means
 * something else entirely — the wire VALUE (`number | string`), where this is
 * the query-string KEY. Two names for two things beats one name that changes
 * meaning with the import.
 */
type IdListFilterKey = (typeof ID_LIST_FILTERS)[number];

/**
 * The bill-date window: `bill_date >= after` and `bill_date <= before`, both
 * **INCLUSIVE**, so the range names the two days at its ends rather than the gap
 * between them.
 *
 * 🔴 **THIS IS THE INVOICE'S "ISSUE DATE" AND THE WIRE DOES NOT CALL IT THAT.**
 * `d_invoices` has no `issue_date` column — it carries `bill_date` and
 * `due_date`, and `issue_date` belongs to a QUOTE. The keys follow the column;
 * only the LABEL follows the business, which is why the column above this table
 * has always read "Bill date".
 *
 * ⚠️ So `issueDateAfter` / `issueDateBefore` — the `/quotes` spelling — are a
 * **422 naming the unrecognised key** here, and `billDate*` is a 422 there. A
 * date control shared between the two pages would have to be handed the key
 * rather than assuming one; these are not two spellings of one field.
 *
 * '' is "no bound" and the key is omitted from the request entirely:
 * `?billDateAfter=` is a 422 on this `.strict()` schema, not "no filter".
 */
const DATE_FILTERS = ['billDateAfter', 'billDateBefore'] as const;
type DateFilter = (typeof DATE_FILTERS)[number];

/**
 * What this page remembers against the account — the id filters, single and
 * multi, and the bill-date window, which together are the whole URL-backed
 * filter surface here.
 *
 * ⚠️ **A multi-id filter is stored as the CSV STRING the URL holds**, so a
 * saved `lineProjectId` needs nothing of its own here: the store round-trips
 * query-string values, and `4,9` is one of those. A saved view naming a single
 * id still reads back identically, which is what let this control become a
 * multi-select without a flag day.
 * ⚠️ **`new` is deliberately absent**: it opens the create dialog, and
 * remembering a command rather than a filter would greet somebody with a blank
 * New Invoice form on every visit.
 *
 * ⚠️ **`lineProjectStatus` IS ALSO ABSENT, and it is the entry somebody will
 * reach for**, because unlike `new` it really is a filter and really does narrow
 * the request. It is not URL-backed — it lives in `useTableFilters`' own state —
 * so adding it here would store a value nothing reads back, and the row would
 * look saved. Making it savable means putting it in the URL first, which means
 * `useTableFilters` becoming a controlled component: a change to the primitive
 * and to five other pages, not a key added to this list.
 *
 * ⚠️ **The SORT is absent too, and that is not an oversight.** It lives in the
 * table hook's own state rather than in the query string, so there is no key
 * here to persist; putting one in would need a second writer of `sort`, which is
 * how two mechanisms for one parameter start disagreeing.
 */
const SAVED_FILTER_KEYS = [...ID_FILTERS, ...ID_LIST_FILTERS, ...DATE_FILTERS] as const;
/** Merged per key rather than all-or-nothing — see `arrivalDecision`. */
const SAVED_VIEW_KEYS = ['view'] as const;
type SavedInvoicesKey = (typeof SAVED_FILTER_KEYS)[number] | (typeof SAVED_VIEW_KEYS)[number];

/**
 * Reads a positive integer out of the query string, ignoring anything else.
 *
 * Blank and malformed values are dropped rather than forwarded: `?statusId=` is
 * a 422 on this endpoint, not "no filter", and a pasted `?ownerId=abc` would
 * take the whole list down with it.
 */
function parseId(raw: string | null): number | null {
  if (!raw) return null;
  return /^[1-9]\d*$/.test(raw) ? Number(raw) : null;
}

/**
 * Reads a comma-separated list of positive integers out of the query string —
 * `?lineProjectId=4,9` — dropping every member that is not one.
 *
 * ⚠️ **Each MEMBER is validated, not the string.** A single bad member would
 * otherwise reach a `.strict()` schema as `expected number, received NaN` and
 * 422 the whole list — which renders as an empty table, so a pasted
 * `?lineProjectId=4,abc` would read as "no invoices bill project 4" rather than
 * as a bad link. Dropping the junk and honouring the rest is the visible answer:
 * the chips show exactly what was applied.
 *
 * ⚠️ **De-duplicated, because two chips for one project cannot be told apart.**
 * `?lineProjectId=4,4` is not known to be refused and is not worth finding out
 * — it also renders as one selection whatever the server does with it, so the
 * URL and the control would disagree.
 *
 * `[]` is the empty selection, and every caller reads it as **no filter**: the
 * key is omitted from the request and deleted from the URL rather than written
 * empty. `parseId`'s own rules apply per member, so `0`, `-1` and `04` are junk
 * here exactly as they are there.
 */
function parseIdList(raw: string | null): number[] {
  if (!raw) return [];
  const ids: number[] = [];
  for (const part of raw.split(',')) {
    const id = parseId(part.trim());
    if (id !== null && !ids.includes(id)) ids.push(id);
  }
  return ids;
}

/**
 * `currency` is validated as exactly `^[A-Z]{3}$` server-side — "usd" is a 422,
 * not folded to uppercase — so anything else in the URL is treated as absent.
 */
function parseCurrency(raw: string | null): string | null {
  if (!raw) return null;
  return /^[A-Z]{3}$/.test(raw) ? raw : null;
}

/**
 * The same treatment for a date bound: anything that is not a real calendar date
 * is dropped rather than forwarded, because the server answers a 422 NAMING the
 * parameter — a pasted `?billDateAfter=2026-02-30` would take the whole list
 * down rather than filtering it.
 *
 * `isRealDate` checks shape AND existence (`2026-02-30` fails the second), and
 * it is the app's one copy of that rule. ⚠️ **No `Date` is ever constructed from
 * one of these values**, here or below: `new Date('2026-05-31')` is midnight UTC
 * and renders as 30 May west of Greenwich. They are compared as strings and
 * formatted by `formatDateOnly`.
 *
 * `''` rather than `null` for "no bound", matching the string these come out of
 * and go back into — the `<input type="date">` value and the query string.
 */
function dateBound(raw: string | null): string {
  return raw !== null && isRealDate(raw) ? raw : '';
}

/**
 * `statuses` is the `/invoice-statuses` master list, and it is threaded down
 * here rather than re-fetched: it carries `isSettled`, which the invoice's own
 * `status` (`{id, name}`) does not. Without it `isOverdue` falls back to
 * matching status names exactly — the guess that flag exists to replace.
 */
function DueDateCell({
  invoice,
  statuses,
}: {
  invoice: Invoice;
  statuses: readonly InvoiceStatus[];
}) {
  return (
    <span className="inline-flex flex-wrap items-center gap-1.5">
      <span className="whitespace-nowrap tabular">{formatDateOnly(invoice.dueDate)}</span>
      {/* A word, not a colour: the badge says "Overdue" so the row still reads
          correctly in greyscale and to a screen reader. */}
      {isOverdue(invoice, statuses) && <Badge tone="warning">Overdue</Badge>}
    </span>
  );
}

function SubjectCell({ invoice }: { invoice: Invoice }) {
  return (
    /*
     * ⚠️ **THIS CELL'S MINIMUM IS WHAT ACTUALLY WIDENS THIS COLUMN — the table's
     * floor ALONE DOES NOT, and that is the trap this cell exists to record.**
     * *SUPERSEDED: `min-w-0`, then `min-w-[280px]`.* Raising the table's floor only bites while the
     * table is AT that floor; on a screen wider than it, the table fills the
     * container and the columns are sized by content instead — so a floor-only
     * change reads as "nothing happened" on exactly the wide screens somebody
     * asks for a wider column from.
     *
     * And under `table-layout: auto` this column loses that contest every time,
     * because `[overflow-wrap:anywhere]` on the link below lets the subject break
     * mid-word: its min-content is one character, so it is the column the
     * algorithm shrinks FIRST while the seven `w-*` columns hold their widths.
     * A long name then wraps to four or five lines with slack still on the row —
     * which is the state this was reported in.
     *
     * A content minimum raises the cell's min-content, which the algorithm may
     * not go under, so the column holds 280 at any container width. It is the
     * line grids' rule (`CLAUDE.md`, "A content `min-w-` on every cell"), and
     * 280 is the 304px column minus `TD`'s `px-3` twice.
     *
     * ⚠️ **It is NOT a `w-` and must not become one.** A width would make this an
     * eighth FIXED column and leave the table with no fluid one, so it would stop
     * absorbing slack on a wide screen — a minimum keeps it fluid ABOVE 280 and
     * merely stops it collapsing below.
     *
     * ✅ **280 AND THE TABLE'S FLOOR ARE ONE NUMBER IN ONE PLACE NOW.**
     * *SUPERSEDED: "280 and the table's 1328 floor are ONE number in two places
     * (1024 of fixed columns + 304). Move them together or the floor stops
     * describing the layout it is meant to guarantee."* They were two literals
     * that had to be moved together, which is the drift the `min-w-[1328px]`
     * comment two thousand lines down already records having suffered once (a
     * column went `w-36` → `w-40` and the floor was not moved with it).
     *
     * `SUBJECT_MIN_CONTENT` is that number, and `COLUMNS[SUBJECT_COLUMN].width`
     * is `SUBJECT_MIN_CONTENT + CELL_PADDING_X` — so the floor is now SUMMED
     * from the same constant this cell is sized by, and a change here moves it
     * automatically.
     *
     * ⚠️ **AN INLINE STYLE, NOT `min-w-[280px]`, AND THAT IS FORCED.** Tailwind
     * v4 scans SOURCE TEXT, so `min-w-[${n}px]` emits no rule at all — the class
     * lands in the DOM and does nothing, which looks exactly like a column that
     * chose not to hold its width. `Table.tsx` and `UniversityPartnershipsPage`
     * both record the same trap; a number goes through `style`.
     */
    <div style={{ minWidth: SUBJECT_MIN_CONTENT }}>
      <TextLink
        to={`/invoices/${invoice.id}`}
        className="text-sm font-medium [overflow-wrap:anywhere]"
      >
        {invoice.subject}
      </TextLink>
      {/* The Xero number is the figure finance quotes on the phone, so it is
          worth a sub-line — but it is nullable and most rows will not have one. */}
      {invoice.xeroInvoiceNumber && (
        <p className="mt-0.5 truncate text-xs text-fg-muted tabular">
          <span className="sr-only">Xero invoice number </span>
          {invoice.xeroInvoiceNumber}
        </p>
      )}
    </div>
  );
}

/*
 * ⚠️ **SUPERSEDED 2026-09-02: "ONE column is declared to the table hook, and the
 * shortness of the list is the decision rather than an unfinished job … Bill
 * date is the only header on this table the API can honestly order by … the
 * other five need no entry and get no funnel."** **FOUR are declared now** —
 * Status, Bill date, Projects and From Xero — and the sentence had already been
 * falsified once (by Projects) before it was falsified again the same day.
 *
 * The rule underneath it is untouched and is the half worth keeping: **a column
 * gets a spec only where the funnel or the sort can do something real.** Invoice,
 * Due date, Grand total and Actions still have none and still fall through
 * `headerFor` to a plain `TH`; see the ordering note at the top for why `subject`
 * is left out and why `invoiceNumber` can never be in.
 *
 * ⚠️ **`scope` on BILL DATE is left at its default `'page'`, and it does NOT
 * compete with the bill-date RANGE in the filter bank.** They are different
 * questions on one column: the range is a server param narrowing the whole
 * table, this funnel picks exact values out of the twenty rows already loaded,
 * and the popover's footnote says which it is doing. `/deals` carries the same
 * pairing on Stage for the same reason. What would be a real conflict is two
 * writers of ONE query key — which is why this column is not `scope: 'server'`
 * with a `param`.
 *
 * ⚠️ **STATUS IS THE OPPOSITE CASE AND IS NOT A COUNTER-EXAMPLE TO THAT.** It
 * shares a query key with a control in the filter bank as well — but it shares
 * the SAME QUESTION, not a neighbouring one, so leaving the two independent
 * would let a reader pick "Paid" in the funnel and "Draft" in the Select and
 * meet an empty table with nothing on screen explaining it. It is therefore
 * BRIDGED to the URL rather than owned by the hook: see `bridgeStatusToUrl`
 * below, which is where the single source of truth is enforced.
 *
 * ── The three unspecced columns, audited against the endpoint 2026-09-09 ────
 *
 * Re-checked key by key against `InvoiceListParams`, because *"make these
 * funnels filter the whole table rather than the loaded page"* is a task that
 * will be put again and this is the answer it arrives at. **Nothing changed.**
 * A column funnel emits ONE EXACT VALUE under ONE query key, and this endpoint
 * has no such key for anything these three cells render:
 *
 * - **Due date** is the near miss and the one to be careful about.
 *   `dueDateAfter` / `dueDateBefore` DO exist and nothing on this page writes
 *   either of them today — so they read as the missing line — but they are a
 *   RANGE PAIR, not an exact value. `param: 'dueDateAfter'` would send a ticked
 *   date as a LOWER BOUND and return every invoice due on or after it, under a
 *   checkbox naming that one day: silently WIDER than it claims, which is the
 *   direction nobody reports. A due-date window belongs in the filter bank
 *   beside the bill-date one, as two date inputs writing two keys.
 * - **Grand total** has no money filter of any kind. `unsettled` and `overdue`
 *   are the adjacent booleans and neither of them is "this amount".
 * - **Invoice** renders `subject` and `xeroInvoiceNumber`. `search` matches
 *   `subject` alone, it is a LIKE rather than the equality a funnel would be
 *   claiming, and the search box already writes it — the two-writers-of-one-key
 *   conflict the Status paragraph above exists to prevent. `xeroInvoiceNumber`
 *   has no filter and can have none, for the same reason it has no sort.
 *
 * ⚠️ **`currency` is the fourth trap and it is not even a column here.** It is a
 * real key on this endpoint, so a Currency funnel looks free — but the "Filter
 * by currency" Combobox in the filter bank writes `?currency=`, so a column
 * owning it would be the Status bug under a different name.
 *
 * The other half of that audit: the three columns that DO query the whole table
 * each declare a COMPLETE `options` list rather than accumulating one out of the
 * loaded rows — Status from the `/invoice-statuses` master (`perPage: 200`,
 * retired rows included), Projects and From Xero from their own modules, both of
 * which build the array from the full vocabulary rather than listing it twice.
 * That needed no change either.
 */
const BILL_DATE_COLUMN = 'billDate';

/**
 * The Status column's key, and it is **deliberately the URL parameter's own
 * name** rather than something like `'status'`.
 *
 * The funnel does not own a filter of its own — it reads and writes
 * `?statusId=`, the very key the "All statuses" Combobox writes. Spelling the
 * column key the same way is what makes that obvious at every site that names
 * it (`headerFor`, `COLUMN_KEYS`, the bridge), rather than leaving a
 * reader to discover the relationship from the bridge alone.
 */
const STATUS_COLUMN = 'statusId';

/**
 * The Projects column's key, spelled once now that it is named in five places —
 * the filter spec, `COLUMN_KEYS`, the chrome table, the header and the body
 * cell. It doubles as the query parameter, like `IMPORTED_FROM_XERO_COLUMN` and
 * unlike `STATUS_COLUMN`, whose column declares no `param` at all.
 */
const LINE_PROJECT_COLUMN = 'lineProjectStatus';

/**
 * Every ticked value gets a checkbox, whether or not the option list knows it.
 *
 * ⚠️ **A SELECTED VALUE WITH NO OPTION CANNOT BE UNTICKED.** The column reads as
 * filtered, the table is narrowed, and the popover offers nothing to switch it
 * off — so the only way out is editing the URL. That is reachable here whenever
 * the `/invoice-statuses` master could not be read and the list falls back to
 * values accumulated from the loaded page.
 *
 * `#<value>` rather than a guessed name: the label is not in hand, and this is
 * the same idiom the "Also filtered by" badges use for an id they cannot name.
 *
 * ⚠️ **A THIRD COPY OF `CompaniesPage`'s helper** (`/quotes` now carries the
 * other). It is a pure four-line function over `ColumnFilterOption[]` and it
 * plainly belongs beside `ColumnFilter` or in `use-table-filters`. **Not moved:
 * both are shared files under edit by other sessions**, and a primitive moved
 * under somebody mid-change is a merge conflict in a file six pages import.
 * Recorded so the next person to touch either does it deliberately.
 */
function withSelectedValues(
  options: ColumnFilterOption[],
  selected: readonly string[],
): ColumnFilterOption[] {
  const known = new Set(options.map((option) => option.value));
  const missing = selected.filter((value) => !known.has(value));
  return missing.length === 0
    ? options
    : [...options, ...missing.map((value) => ({ value, label: `#${value}` }))];
}

/**
 * The four filterable columns, built per render because ONE of them needs data.
 *
 * ⚠️ **This is a factory rather than a module constant because Status's
 * `options` are the `/invoice-statuses` master**, which is fetched. Left to
 * accumulate from the loaded rows the popover would offer only the statuses the
 * pages somebody had already looked at — and once a status filter is applied the
 * next response holds that status alone, so the funnel would become one-way. The
 * hook's `seenServerValues` exists for exactly that, but a CLOSED master read
 * from the API is better than a set inferred from twenty rows.
 *
 * The result is memoised at the call site; `useTableFilters` takes `columns` as
 * an effect dependency, and a fresh array every render costs an extra settling
 * pass.
 *
 * 🔴 **`statusOptions` IS `undefined` WHILE THE MASTER HAS RETURNED NOTHING, AND
 * THAT IS NOT THE SAME AS `[]`.** `ColumnSpec.options` REPLACES the accumulated
 * list outright, so an empty array handed over after a failed read would render
 * "No values on this page" over a table of invoices that every one have a
 * status — a closed set asserted from a request that never came back. `undefined`
 * hands the column back to the hook's accumulated values, which are narrower than
 * the master but are at least derived from rows actually on screen. The Combobox
 * beside the funnel is what says the read failed, in words.
 */
const invoiceColumns = (statusOptions: ColumnFilterOption[] | undefined): ColumnSpec<Invoice>[] => [
  /**
   * Status — **the funnel half of a filter the page already had**.
   *
   * ⚠️ **`scope: 'server'` WITH NO `param`, and the omission is the whole
   * design.** `useTableFilters` skips a server column that declares no `param`
   * when it builds `serverParams`, so this column can never emit a query key of
   * its own — and `visibleRows` returns early for a server column, so it never
   * re-filters the loaded page either. The SELECTION lives in the URL and
   * nowhere else; `bridgeStatusToUrl` supplies `value` and `onChange` from
   * there. What the hook still holds for this key is the EMPTINESS toggle, which
   * is page-scoped because no endpoint here exposes an is-null filter.
   *
   * 🔴 **DO NOT "FINISH" THIS BY ADDING `param: 'statusId'`.** It looks like the
   * missing line and it is the bug: the hook would then hold a second copy of
   * the selection, `serverParams.statusId` would be merged into the request
   * beside the URL's own `statusId` written by the Select, and the two would
   * disagree the moment anybody used both controls — silently, because whichever
   * `Object.assign` ran last would win and the funnel would keep showing the
   * other one.
   *
   * ✅ **SEVERAL STATUSES SINCE 2026-10-28.** *SUPERSEDED: "`?statusId=1,2` IS A
   * 422 … the bridge reduces a multi-tick to a single id".* The bridge now writes
   * every ticked id to the URL as one CSV. `multi` stays absent all the same,
   * because the column still declares no `param` — the URL is the one writer.
   *
   * `get` returns the status ID, matching the option values
   * (`selectableStatuses` keys on `String(status.id)`), so the emptiness test
   * reads "has no status" correctly on a nullable column.
   */
  {
    key: STATUS_COLUMN,
    label: 'Status',
    get: (invoice) => invoice.status?.id ?? null,
    // Spread rather than assigned: `options: undefined` and an absent key are
    // the same thing to the hook, but writing the key unconditionally invites
    // the next reader to "fix" it into an empty array. See the block above.
    ...(statusOptions ? { options: statusOptions } : {}),
    scope: 'server',
    sortKey: 'status' satisfies InvoiceListSort,
  },
  {
    key: BILL_DATE_COLUMN,
    label: 'Bill date',
    get: (invoice) => invoice.billDate,
    // The stored 'YYYY-MM-DD' is the filter VALUE; the reader sees it formatted,
    // exactly as the cell renders it. No `Date` is constructed either side.
    format: (value) => formatDateOnly(value),
    /*
     * `satisfies` rather than a bare string: it pins the key to the endpoint's
     * own union, so a rename or a typo is a `tsc` error here instead of a 422
     * naming an unrecognised key at runtime.
     */
    sortKey: 'billDate' satisfies InvoiceListSort,
  },
  /**
   * "Projects added / not added", asked of the invoice's LINE ITEMS.
   *
   * ⚠️ **`scope: 'server'`, unlike its neighbour above, and the contrast is the
   * reason both are right.** Bill date is page-scoped BECAUSE the filter bank
   * already owns `billDateAfter`/`billDateBefore` — two writers of one query key
   * is the conflict that note warns about. Nothing else writes
   * `lineProjectStatus`, so this column can own it outright; and it must, since
   * the list is paginated and a page-scoped answer to "which invoices are
   * missing projects" is an answer about twenty rows under a header that implies
   * eight hundred.
   *
   * ⚠️ **`options` is DECLARED, not accumulated from the loaded rows.** Left to
   * derive itself, the popover would offer only the states already on screen —
   * and on this endpoint that is overwhelmingly `none`, because the Xero
   * importer writes line items and never writes a project. "Partly added" would
   * be unreachable exactly where it is worth finding.
   *
   * `satisfies InvoiceListSort` for the same reason Bill date carries it: a
   * rename becomes a `tsc` error rather than a 422 that empties the table.
   */
  {
    key: LINE_PROJECT_COLUMN,
    label: 'Projects',
    get: (invoice) => invoice.lineProjectStatus,
    format: lineProjectStatusLabel,
    options: LINE_PROJECT_FILTER_OPTIONS,
    scope: 'server',
    param: LINE_PROJECT_COLUMN,
    // Several states go out as one CSV and OR (2026-10-28).
    multi: true,
    sortKey: 'lineProjectStatus' satisfies InvoiceListSort,
  },
  /**
   * "From Xero" — *did this invoice come in from the Xero history import, or was
   * it raised here* — as Yes / No.
   *
   * ⚠️ **`scope: 'server'` with a `param` of its own, like Projects above and
   * unlike Status.** Nothing else on this page writes `importedFromXero`, so the
   * column owns it outright — and it must, because the list is paginated and a
   * page-scoped answer to "which of these came from Xero" is an answer about
   * twenty rows under a header that implies hundreds.
   *
   * 🔴 **THIS COLUMN WAS BUILT `scope: 'page'` FIRST, ON A MEASUREMENT THAT WAS A
   * FEW MINUTES OLD.** `src/lib/imported-from-xero.ts` had read the backend
   * before the filter landed — including that repo's uncommitted diff — and said
   * in the strongest available words that no such param existed and that wiring
   * one would empty the table. It was honest and it was wrong by the time it was
   * read. **Nothing in this file could have caught that**; it was settled by
   * asking, and the corrected module now carries both the measurement and the
   * date. Read it rather than re-deriving either half here.
   *
   * ⚠️ **`?importedFromXero=false` GENUINELY NARROWS.** It is the other half of
   * a real partition — the column is a timestamp or `NULL`, with no third state
   * — so "No" is a query, not "no filter", and omitting the key is a THIRD
   * request distinct from both. `listQuery()` keeps a `false` for exactly this.
   * Its neighbours on that schema (`unsettled`, `overdue`) do NOT work this way.
   *
   * ⚠️ **`get` speaks the WIRE, `format` speaks the reader's word.** A chosen
   * option's `value` goes into the query param verbatim, so `get` emitting
   * `'Yes'` would send `?importedFromXero=Yes`, which `queryBoolean()` refuses —
   * a 422 that reads as "nothing matches". The CELL below uses
   * `importedFromXeroLabel` instead, which takes the raw field.
   *
   * ⛔ **NO `multi`** — `?importedFromXero=true,false` is a 422, as is every CSV
   * filter outside `/companies`. With both ticked the hook deliberately sends
   * nothing and narrows the loaded page, comparing the option values against
   * this `get`; that is the second reason the two must share one vocabulary.
   */
  {
    key: IMPORTED_FROM_XERO_COLUMN,
    label: 'From Xero',
    get: (invoice) => importedFromXeroValue(invoice.importedFromXeroAt),
    format: importedFromXeroFormat,
    options: IMPORTED_FROM_XERO_FILTER_OPTIONS,
    scope: 'server',
    param: IMPORTED_FROM_XERO_COLUMN,
    sortKey: 'importedFromXero' satisfies InvoiceListSort,
  },
  /*
   * "Sent to client" — server-scoped on the same terms as From Xero directly
   * above: `?sentToClient=false` genuinely narrows (the other half of a real
   * partition), `get` speaks the wire, and there is NO `multi` (a CSV is a 422).
   *
   * ⚠️ **An older API sends no `sentToClient` at all.** `sentToClientValue`
   * answers `''` for that, which matches neither option — so a page-scoped
   * fallback never counts an unreported row as "No".
   */
  {
    key: SENT_TO_CLIENT_COLUMN,
    label: SENT_TO_CLIENT_LABEL,
    get: (invoice) => sentToClientValue(invoice.sentToClient),
    format: sentToClientFormat,
    options: SENT_TO_CLIENT_FILTER_OPTIONS,
    scope: 'server',
    param: SENT_TO_CLIENT_COLUMN,
    sortKey: 'sentToClient' satisfies InvoiceListSort,
  },
];

/*
 * ── THE TABLE'S COLUMNS, AS ONE LIST ────────────────────────────────────────
 *
 * ✅ **ONE ORDERED ARRAY DRIVES THE HEADER, THE BODY, THE SKELETON, THE MOBILE
 * FILTER SHEET AND THE TABLE'S MINIMUM WIDTH.** *SUPERSEDED: three hardcoded
 * cell lists — the header row, `TableSkeleton` and the body `<TR>` — plus
 * `INVOICE_COLUMN_KEYS`, a fourth hand-maintained copy of the same order kept
 * only for the mobile sheet.*
 *
 * ⚠️ **FOUR PARALLEL LISTS IS THE DEFECT THIS REPLACES, AND EVERY ONE OF THEM
 * HAD ALREADY DRIFTED.** The skeleton's own comments record a cell outliving its
 * column and another arriving late; `INVOICE_COLUMN_KEYS`' comment records the
 * sheet falling out of step with the header; and the `min-w-[1328px]` literal
 * was stale by 16px for nine days, which its own comment admits in place.
 * **None of those failures errors, and none is visible to `tsc`** — a missing
 * skeleton cell just shifts every column after it as the rows arrive.
 *
 * ⛔ **COLUMN KEYS NEVER JOIN `SAVED_FILTER_KEYS`.** That array feeds the URL
 * parse loops and `useSavedFilters`' whole-set replace; the layout has its own
 * store, against the account, keyed `preferences.columns.invoices`. See the
 * import note at the top of this file.
 */
const SUBJECT_COLUMN = 'subject';
const DUE_DATE_COLUMN = 'dueDate';
const GRAND_TOTAL_COLUMN = 'grandTotal';
const ACTIONS_COLUMN = 'actions';

/** Every column this table declares, in its DECLARED order. */
const COLUMN_KEYS = [
  SUBJECT_COLUMN,
  STATUS_COLUMN,
  BILL_DATE_COLUMN,
  LINE_PROJECT_COLUMN,
  IMPORTED_FROM_XERO_COLUMN,
  SENT_TO_CLIENT_COLUMN,
  DUE_DATE_COLUMN,
  GRAND_TOTAL_COLUMN,
  ACTIONS_COLUMN,
] as const;

type InvoiceColumnKey = (typeof COLUMN_KEYS)[number];

/**
 * 🔴 **ACTIONS IS LOCKED: it cannot be hidden and it cannot be moved.**
 *
 * It is the only route on this list to the PDF download, Edit and Delete — and
 * `canDownloadPdf` is ungated by role, so a `viewer` reaches their only control
 * through it too. A reader who hid it would lose all three with nothing on
 * screen saying where they went, and the layout persists against the account, so
 * it would still be gone tomorrow and on their other machine.
 *
 * ⚠️ **A locked key is still LISTED in the manager**, disabled, with the reason
 * on its `title` — `ColumnManager` does that itself. Omitting it would make the
 * column's absence from the list read as the feature having lost it, which is
 * the "a missing affordance is indistinguishable from a removed feature" rule
 * the kanban boards are already built around.
 */
const LOCKED_COLUMN_KEYS = [ACTIONS_COLUMN] as const;

/**
 * Columns this page renders with `pinned` — `sticky right-0`, the one-column
 * special case of `frozen="right"` at offset 0.
 *
 * ⚠️ **IT IS NOT `LOCKED_COLUMN_KEYS` WEARING A SECOND NAME, even though the two
 * hold the same single key today.** Locked answers *may this column be hidden or
 * moved*; pinned answers *which edge does this column already occupy*, and the
 * second is the only one that may stop a column being frozen. Conflating them
 * made the identifying column — locked on most list pages, and the single best
 * freeze-LEFT candidate anywhere — unfreezable. Invoice is not locked here
 * today, so nothing on this page is currently harmed by the confusion; the sets
 * are kept apart anyway, because the day somebody locks Invoice is the day the
 * bug would arrive with nothing to explain it.
 *
 * Handed to three places, which must agree:
 * - `useColumnLayout`, so `setFrozen` refuses a second, conflicting position
 *   rule on a cell that already has one;
 * - `ColumnManager`, so that column offers no freeze buttons;
 * - `useFrozenOffsets`, so the lane's rendered width is COUNTED and the next
 *   right-frozen column stacks beside it rather than underneath it.
 */
const PINNED_COLUMN_KEYS = [ACTIONS_COLUMN] as const;

/**
 * Why a locked column cannot be hidden, in the reader's own words.
 *
 * ⚠️ **A why-this-is-disabled sentence, which the no-helper-text prohibition
 * keeps by name** — without it a locked tickbox and a broken one are the same
 * control. Per key rather than one fixed string: Actions is locked because it
 * carries CAPABILITIES, and the next column locked here will be locked for some
 * other reason.
 */
function lockReasonOfColumn(key: string): string | undefined {
  return key === ACTIONS_COLUMN
    ? 'Download PDF, Edit and Delete are only here, so this column always shows'
    : undefined;
}

/**
 * The Invoice column's content minimum — see `SubjectCell`, which is sized by
 * this and is the reason the number exists at all.
 */
const SUBJECT_MIN_CONTENT = 280;

/** `TD`/`TH` carry `px-3`, twice. */
const CELL_PADDING_X = 24;

interface InvoiceColumnChrome {
  /** The header's own words, and the label the column manager lists it under. */
  label: string;
  /**
   * What this column contributes to the table's minimum width, in px.
   *
   * ⚠️ **A FLOOR SUMMED FROM THE COLUMNS ACTUALLY RENDERED, not a literal
   * carried forward by deltas.** *SUPERSEDED: `min-w-[1328px]`, and the comment
   * beside it which walked through six additions and subtractions and then
   * admitted to being 16px stale.* Hiding a column now shrinks the table by
   * exactly that column, because the same array decides both.
   *
   * ⚠️ **THE SEVEN FIXED FIGURES ARE THE `w-*` CLASSES IN PX** — `w-28` is 112,
   * `w-32` is 128, `w-40` is 160, `w-44` is 176 — and they must move together
   * with the class beside them. `subject` is the odd one out and is DERIVED:
   * it is the one FLUID column, so its floor is its cell's content minimum plus
   * the padding, which is where 304 comes from and why it is not written down.
   */
  width: number;
  /**
   * The width class, on the header cell only.
   *
   * ⚠️ **Invoice deliberately has NONE.** It is the single fluid column and takes
   * whatever the seven fixed ones leave; a `w-` here would make it an eighth
   * FIXED column and leave the table with nothing to absorb slack on a wide
   * screen, so it would stop filling the width and start truncating again at the
   * next long name.
   */
  className?: string;
  numeric?: boolean;
}

const COLUMNS: Record<InvoiceColumnKey, InvoiceColumnChrome> = {
  [SUBJECT_COLUMN]: { label: 'Invoice', width: SUBJECT_MIN_CONTENT + CELL_PADDING_X },
  // `w-40` is unchanged by the funnel — `FilterableTH` renders its trigger
  // INSIDE the cell, so gaining one does not widen the column.
  [STATUS_COLUMN]: { label: 'Status', width: 160, className: 'w-40' },
  [BILL_DATE_COLUMN]: { label: 'Bill date', width: 128, className: 'w-32' },
  /*
   * ⚠️ **`w-40` HOLDS THE LONGEST LABEL — "No projects apply" — AND THERE ARE
   * FIVE, NOT FOUR.** *SUPERSEDED: `w-36`, "the longest of the four labels ('No
   * line items')".* A count and a width sized to one label, both falsified by
   * the fifth state added on 2026-09-02. The badge truncates to its `title`
   * rather than breaking the row, so that read as clipped rather than failing.
   * Matches `/quotes`, which carries the same column at the same width. **Do not
   * write the count back in** — size from the longest label in `LABELS`.
   */
  [LINE_PROJECT_COLUMN]: { label: 'Projects', width: 160, className: 'w-40' },
  // `w-32` holds "From Xero" plus the funnel trigger on one line at `--text-sm`;
  // `w-28` truncates the label.
  [IMPORTED_FROM_XERO_COLUMN]: { label: 'From Xero', width: 128, className: 'w-32' },
  // `w-40` holds "Sent to client" plus the funnel trigger, and the cell's
  // "Sent · <date>" on one line at `--text-xs`.
  [SENT_TO_CLIENT_COLUMN]: { label: SENT_TO_CLIENT_LABEL, width: 160, className: 'w-40' },
  [DUE_DATE_COLUMN]: { label: 'Due date', width: 176, className: 'w-44' },
  [GRAND_TOTAL_COLUMN]: { label: 'Grand total', width: 160, className: 'w-40', numeric: true },
  /*
   * ✅ **RE-BASED TO THE WORST CASE — 208, owner's decision 2026-09-19.**
   * *SUPERSEDED: `width: 144` and its matching three-control `w-` class, and
   * with them the sentence "Re-basing to 208 would be a different decision
   * about how this column is sized, taken on nobody's instruction".* It was
   * put to the owner with its cost and they took it. The two figures are one
   * figure written twice — the px is what `tableMinWidth` sums, the class is
   * what the header cell asks for — so they move together or the floor stops
   * describing the table.
   *
   * ⚠️ **THE DECLARED FIGURE WAS A TYPICAL CASE AND THE LANE ALWAYS LAID OUT
   * AT ITS WORST ONE.** Measured: an `iconOnly size="sm"` button is `h-8 w-8`
   * (32px), the bank is `gap-1.5` (6px), and `TD` carries `px-3` twice (24px)
   * — so FIVE controls at once need `24 + 5×32 + 4×6 = 208`, and the four that
   * preceded them needed 170 against the 112 then declared. **The column was
   * declaring the THREE-control width for a five-control lane**, which is the
   * sharper statement of it: on `CompaniesPage`'s own `ACTION_COL_WIDTHS`
   * scale (80 · 112 · 144 · 176 · 208 · 240), 144 is index 3.
   *
   * 🔴 **NOTHING EVER CLIPPED, AND THE REASON IS NOT THE ONE THIS COMMENT USED
   * TO GIVE.** *SUPERSEDED: "survivable only because there is no `table-fixed`
   * and no `<colgroup>` here".* True, and **insufficient** — without
   * `table-fixed` a column can still be squeezed toward a smaller min-content
   * and the buttons would overlap. What actually guarantees it is **`shrink-0`
   * on `Button`'s base class**: the icon buttons are incompressible flex
   * items, so the cell's min-content is exactly 208 and the column could never
   * settle below it. **The safety is structural, in the primitive, not a
   * property of this table's layout mode** — say so wherever this is argued
   * next.
   *
   * ⚠️ **WHAT THE RE-BASE ACTUALLY FIXES is the load-time reflow, which is the
   * symptom a reader sees.** The skeleton's actions cell draws TWO `h-6 w-6`
   * placeholders (min-content ≈ 78), so while loading the declared width won
   * uncontested at 144 and the column then JUMPED ~64px when the rows arrived
   * with five controls. Declared now equals laid-out, so it does not move.
   * That inverts nothing: `showActions` is unconditional at the call site
   * precisely so the skeleton cannot be NARROWER than the rows replacing it,
   * and at 144 it was anyway.
   *
   * ⚠️ **THE ACCEPTED COST, STATED: 208 is a STATIC worst case, so every
   * reader reserves 64px more than before, including one who never sees five
   * controls** — without `invoices.update` the lane holds at most three. ⛔
   * **There is no `ACTION_COL_WIDTHS` array on THIS page**, and adopting one
   * is the larger, better change rather than a tidy-up: `QuotesPage` refused
   * it on the grounds that a width following the reader's own permissions
   * makes one page two widths for two people.
   *
   * ⚠️ **`/quotes` IS UNDER-DECLARED TOO — 224 against a six-control need of
   * 246** (measured 2026-09-19). Untouched here; it is a separate declaration
   * with a separate control count, and nobody has asked for it.
   */
  [ACTIONS_COLUMN]: { label: 'Actions', width: 208, className: 'w-52', numeric: true },
};

/** The manager's label for a key — the same word the header shows. */
function columnLabel(key: string): string {
  return COLUMNS[key as InvoiceColumnKey]?.label ?? key;
}

/**
 * One rendered column: its key, plus wherever the layout has frozen it to.
 *
 * `pinned` and `frozen` are mutually exclusive here and Actions is the only
 * column that ever takes `pinned` — see `frozenPropsOf`.
 */
interface RenderedColumn {
  key: InvoiceColumnKey;
  pinned?: boolean;
  frozen?: FrozenSide;
  frozenOffset?: number;
}

/**
 * The sticky props for one cell, spread onto both the `TH` and the `TD` so the
 * two can never disagree about which edge a column is on.
 */
function frozenPropsOf(column: RenderedColumn): {
  pinned?: boolean;
  frozen?: FrozenSide;
  frozenOffset?: number;
} {
  if (column.pinned) return { pinned: true };
  if (column.frozen === undefined) return {};
  return { frozen: column.frozen, frozenOffset: column.frozenOffset };
}

/**
 * The table's minimum width: the sum of the columns actually on screen.
 *
 * ⚠️ **IT GOES THROUGH `style`, NOT A CLASS.** Tailwind v4 scans SOURCE TEXT, so
 * `min-w-[${n}px]` emits no rule whatsoever — the class lands in the DOM and
 * does nothing, which looks exactly like a table that decided not to hold its
 * floor. Both `Table.tsx` and `UniversityPartnershipsPage` record the same trap.
 *
 * ✅ **It sits on the `<Table>` itself — `Table` takes a `style` now.**
 * *SUPERSEDED: "It sits on a wrapper `<div>` rather than on `<Table>`, because
 * `Table` takes no `style` prop and it is a shared primitive."* The table is the
 * box that overflows `TableWrap`'s `overflow-x-auto` and produces the horizontal
 * scroll.
 *
 * ⚠️ *SUPERSEDED, the tail of that sentence: "at the same used width the old
 * literal on the table produced".* It was the same width **on the day of the
 * migration** and has not been since — Actions moved 112 → 144 → 208, so the
 * default set is 96px wider than the literal ever was. **A sentence asserting
 * two numbers are equal is falsified by either of them moving**, and only one
 * of the two is still computed. ⛔ The dead literal is deliberately not spelled
 * here: Tailwind v4 scans source text, comments included, so writing a complete
 * arbitrary-value class in prose emits a rule nothing uses — and defeats anyone
 * grepping the served bundle to prove it has gone.
 */
function tableMinWidth(keys: readonly string[]): number {
  return keys.reduce((sum, key) => sum + (COLUMNS[key as InvoiceColumnKey]?.width ?? 0), 0);
}

/**
 * One header cell.
 *
 * ✅ **ONE PATH, NOT THREE — `FilterableTH` TAKES `frozen`/`frozenOffset` NOW
 * (2026-09-12).** *SUPERSEDED: a module-local `FrozenFilterableTH` re-spelling
 * `FilterableTH`'s markup so a frozen column could be handed its measured px
 * offset, plus the branch here that chose between the two. Its own comment set
 * the exit condition — "DELETE THIS THE DAY `FilterableTHProps` GAINS
 * `frozen`/`frozenOffset`" — and this is that.* The `aria-sort` derivation went
 * with it and is byte-identical inside the primitive, which is the point: the
 * copy was already one prop behind (`wrap`, the same week), and a local
 * re-spelling is exactly what misses the next improvement silently.
 *
 * ⚠️ **THE LABEL COMES FROM `COLUMNS`, NEVER FROM `headerFor`.** `useTableFilters`
 * returns `{ label: key }` for a key it has no spec for, so Invoice, Due date,
 * Grand total and Actions would render their own KEYS as headings. Reading the
 * chrome table for every column also means the header and the column manager
 * cannot list one column under two different words.
 *
 * ⚠️ **Whether a column has a funnel is `headerFor(key).filter`, not a list.**
 * That is the same test `FilterSheet` applies, so the desktop header and the
 * mobile sheet can never disagree about which columns are filterable. A column
 * with no filter falls through to `FilterableTH`'s own plain-`TH` branch, which
 * is why there is no third case here either.
 */
function InvoiceHeadCell({
  column,
  headerFor,
}: {
  column: RenderedColumn;
  headerFor: (key: string) => FilterableTHProps;
}) {
  const chrome = COLUMNS[column.key];
  const { filter } = headerFor(column.key);

  return (
    <FilterableTH
      label={chrome.label}
      numeric={chrome.numeric}
      className={chrome.className}
      {...frozenPropsOf(column)}
      filter={filter}
    />
  );
}

/**
 * The header row — and the element `useFrozenOffsets` measures every column's
 * rendered width from.
 *
 * ⚠️ **SUPERSEDED 2026-09-02, and then again the same day: "No sortable headers
 * anywhere in here", then "Bill date is sortable now. The rest of this row is
 * still plain `TH`".** FOUR of the eight headers carry a funnel and a sort. The
 * four that do not — Invoice, Due date, Grand total and Actions — fall through
 * to a plain `TH`; see the ordering note at the top of this file for which
 * columns the API can order by and which one (`invoiceNumber`) it never will.
 *
 * ⚠️ **THE STATUS FUNNEL AND THE "All statuses" COMBOBOX IN THE FILTER BANK ARE
 * ONE FILTER, NOT TWO.** Both read and write `?statusId=`; the funnel is bridged
 * onto it by `bridgeStatusToUrl` rather than owning state in the table hook, so
 * setting one moves the other and they cannot disagree. **Do not "simplify" this
 * into an independent column filter** — that would let a reader pick Paid here
 * and Draft there and meet an empty table with nothing on screen explaining it.
 * The Combobox stays because it is the ONLY status control in board view:
 * funnels live on table headers, and `?view=board` has none. The companies page
 * learned that the hard way, where removing a standalone Select left copy naming
 * a control the board could not reach.
 *
 * ⚠️ **"From Xero" is its own column since 2026-09-02, on the owner's request**,
 * and it used to be a BADGE inside the Status cell — which is why that cell is
 * now a lone `StatusBadge`. Two facts were sharing one column, and the imported
 * one had no header of its own to be filtered or sorted from. ⚠️ **"No" DOES NOT
 * MEAN "not in Xero"** — it means *created in EMERGE rather than imported*, and a
 * document raised here and then pushed is in Xero and still reads "No". Nothing
 * on screen says so and nothing may (no helper text); the distinction lives in
 * `src/lib/imported-from-xero.ts`, which is also why the header is not reworded
 * to something more precise than the owner asked for.
 *
 * ⚠️ **THE `w-44` XERO COLUMN WAS HERE AND WENT ON 2026-08-31, ON THE OWNER'S
 * INSTRUCTION** — the same removal was made on `/quotes` in the same change. It
 * carried `XeroRowPushControl`: an In Xero / Not in Xero badge over a Push/Sync
 * button. His reasoning, in his words: **"The people who will be using this know
 * that it is going to Xero directly."** 🔴 **THE COST, RECORDED RATHER THAN
 * SOLVED — it is a real loss and it was accepted.** That badge was the ONLY place
 * in this list where you could see an invoice had NOT reached Xero; a push is
 * skipped outright when the invoice has no line items or its company is not
 * linked, and it can fail, and after the removal all three look identical to a
 * row that synced cleanly. ⚠️ **Do NOT compensate by putting an indicator
 * somewhere else on this page.** If the blind spot turns out to matter, it goes
 * back to the owner as a decision, not in as a workaround.
 */
function InvoicesTableHead({
  columns,
  headerFor,
  rowRef,
}: {
  columns: readonly RenderedColumn[];
  headerFor: (key: string) => FilterableTHProps;
  /**
   * The element `useFrozenOffsets` measures every column's rendered width from.
   *
   * ✅ **IT ATTACHES TO `TR` DIRECTLY — `TRProps` CARRIES A `ref` NOW
   * (2026-09-12).** *SUPERSEDED: a wrapper `<div>` around the whole table which
   * found this row with `querySelector<HTMLTableRowElement>('thead > tr')`,
   * because no `ui` table part accepted a ref and `Table.tsx` was not that
   * change's to edit.* There is no `table-fixed` and no `<colgroup>` here, so a
   * `w-*` on a `<th>` is a request the content may outvote and the DOM is the
   * only honest source for a frozen column's offset.
   */
  rowRef: Ref<HTMLTableRowElement>;
}) {
  return (
    <THead>
      <TR ref={rowRef}>
        {columns.map((column) => (
          <InvoiceHeadCell key={column.key} column={column} headerFor={headerFor} />
        ))}
      </TR>
    </THead>
  );
}

/*
 * ⚠️ **THE WIDTH CLASSES MOVED INTO `COLUMNS` AND DID NOT CHANGE.** Each header
 * used to carry its own `className="w-40"` beside a note saying why; those notes
 * are now at the entry in `COLUMNS`, where the px figure the table's floor is
 * summed from sits beside the class it has to agree with. The one that was worth
 * keeping in both places — `FilterableTH` renders its trigger INSIDE the cell,
 * so gaining a funnel does not widen a column — is at the Status entry.
 */

/*
 * Pinned to the right edge of the scroller: 904px of table against a content
 * area of the viewport minus the ~256px sidebar puts this column off-screen from
 * ~1160 down — a much narrower window than the 1256px/1520 this said while the
 * Xero column was here, but the pinning is kept: it costs nothing and the table
 * still overflows a laptop beside a sidebar.
 *
 * ✅ **PINNED IN ONE PLACE NOW, not three.** *SUPERSEDED: "Pinned in three places
 * — here, `TableSkeleton` and the body cell."* `frozenPropsOf` is that one place,
 * and the header, the skeleton cell and the body cell all spread it.
 */
/**
 * The placeholder bar for one column, in the SHAPE the real cell renders.
 *
 * ✅ **DRIVEN BY THE SAME ARRAY AS THE HEADER AND THE BODY.** *SUPERSEDED: a
 * hardcoded run of eight `<TD>`s whose own comments record both halves of the
 * failure that is now structurally unreachable — "a cell missing here shifts
 * every column after it as the rows arrive" (From Xero, added late) and "a
 * skeleton cell outliving its column" (the Xero column, removed).*
 *
 * ⚠️ **THE SHAPES ARE NOT INTERCHANGEABLE AND ARE THE POINT OF THIS FUNCTION.**
 * Status and Projects are BADGES, so they need the `h-4` a `Badge` occupies;
 * From Xero is a WORD and takes a text-height bar; Grand total is right-aligned
 * and needs `ml-auto`. A uniform bar everywhere would still jump.
 */
function SkeletonCellContent({
  columnKey,
  showActions,
}: {
  columnKey: InvoiceColumnKey;
  showActions: boolean;
}) {
  switch (columnKey) {
    case SUBJECT_COLUMN:
      return (
        <div className="grid gap-1.5">
          <Skeleton className="h-3 w-48" />
          <Skeleton className="h-2.5 w-24" />
        </div>
      );
    case STATUS_COLUMN:
    case LINE_PROJECT_COLUMN:
      // A BADGE, unlike the dates either side, so the bar stands in for the
      // real shape rather than for a line of text.
      return <Skeleton className="h-4 w-24" />;
    case BILL_DATE_COLUMN:
    case DUE_DATE_COLUMN:
      return <Skeleton className="h-3 w-20" />;
    case IMPORTED_FROM_XERO_COLUMN:
      // A WORD, not a badge, so it is a text-height bar rather than the `h-4` a
      // `Badge` needs.
      return <Skeleton className="h-3 w-10" />;
    case SENT_TO_CLIENT_COLUMN:
      // A word and a date, as text.
      return <Skeleton className="h-3 w-20" />;
    case GRAND_TOTAL_COLUMN:
      return <Skeleton className="ml-auto h-3 w-24" />;
    case ACTIONS_COLUMN:
      return (
        <div className="flex justify-end gap-1.5">
          <Skeleton className="h-6 w-6" />
          {/* ⚠️ *SUPERSEDED: "The second placeholder only when a second control
              is actually coming — otherwise the skeleton is wider than the row
              it stands in for."* **`showActions` is passed unconditionally by
              the one caller**, so this guard cannot fire and the sentence
              describes a conditionality that is gone; the call site records the
              reversal and its reason. Kept as a prop rather than inlined
              because `TableSkeleton` still declares it.

              ⚠️ **TWO BARS STAND IN FOR UP TO FIVE CONTROLS, AND THAT NO LONGER
              MOVES THE COLUMN.** Until 2026-09-19 the lane declared 144 while
              laying out at 208, so the skeleton's ~78px min-content lost to the
              declaration and the column JUMPED when the rows arrived. The
              re-base to the worst case makes declared equal laid-out, so the
              width is settled before the first row. What is left is a
              cosmetic mismatch in the bar COUNT, not a reflow.

              The Xero placeholder, a status badge over a button, stood beside
              these until 2026-08-31 and went with its column. */}
          {showActions && <Skeleton className="h-6 w-6" />}
        </div>
      );
  }
}

function TableSkeleton({
  columns,
  showActions,
}: {
  columns: readonly RenderedColumn[];
  showActions: boolean;
}) {
  return (
    <TBody>
      {Array.from({ length: SKELETON_ROWS }, (_, i) => (
        <TR key={i}>
          {columns.map((column) => (
            <TD
              key={column.key}
              numeric={COLUMNS[column.key].numeric}
              {...frozenPropsOf(column)}
            >
              <SkeletonCellContent columnKey={column.key} showActions={showActions} />
            </TD>
          ))}
        </TR>
      ))}
    </TBody>
  );
}

/**
 * The `TD`'s own props for one body cell — everything that is not the content.
 *
 * Kept beside the content below rather than folded into `COLUMNS`, because two
 * of these depend on the ROW: the From Xero `title` carries that invoice's
 * import timestamp, and there is nothing to say on a row that has none.
 *
 * ⚠️ **A `title` carrying a value the cell does not show is the documented
 * exception to the no-helper-text rule**, and dropping it would lose the only
 * place a person can see WHICH import run wrote a document.
 */
/**
 * The Sent cell's text. FOUR states, read off two fields:
 * `true` + date → "Sent · <date>"; `true` + `null` → "Sent" (Xero flagged it
 * from inside Xero and records no date); `false` → an em dash; `undefined`
 * (an older API) → an em dash too, told apart by the `title` below.
 *
 * ⛔ **`undefined` is NEVER "No".** An unreported field is an unanswered
 * question, not a claim that the invoice was not sent.
 */
function sentCellText(invoice: Invoice): string {
  if (invoice.sentToClient !== true) return '—';
  const at = invoice.sentToClientAt;
  return at === null || at === undefined ? 'Sent' : `Sent · ${formatSentToClientDate(at)}`;
}

/** The Sent cell's `title`, or `null` when there is nothing to add. */
function sentCellTitle(invoice: Invoice): string | null {
  if (invoice.sentToClient === undefined) return 'Not reported by this server';
  return sentToClientTitle(invoice.sentToClient, invoice.sentToClientAt);
}

function bodyCellPropsOf(
  columnKey: InvoiceColumnKey,
  invoice: Invoice,
): { className?: string; title?: string } {
  switch (columnKey) {
    // Dates are plain 'YYYY-MM-DD'; `formatDateOnly` is timezone-safe, and
    // `new Date()` on one of these drifts a day for anyone west of Greenwich.
    case BILL_DATE_COLUMN:
      return { className: 'whitespace-nowrap text-xs text-fg-muted tabular' };
    case IMPORTED_FROM_XERO_COLUMN:
      return {
        className: 'text-xs text-fg-secondary',
        ...(invoice.importedFromXeroAt !== null
          ? { title: `Imported from Xero on ${formatDateTime(invoice.importedFromXeroAt)}` }
          : {}),
      };
    case SENT_TO_CLIENT_COLUMN: {
      const title = sentCellTitle(invoice);
      return {
        className: 'whitespace-nowrap text-xs text-fg-secondary',
        ...(title !== null ? { title } : {}),
      };
    }
    case DUE_DATE_COLUMN:
      return { className: 'text-xs text-fg-muted' };
    case GRAND_TOTAL_COLUMN:
      return { className: 'text-fg-secondary' };
    default:
      return {};
  }
}

/* -------------------------------------------------------------------------
 * RAISING A CREDIT NOTE FROM AN INVOICE
 *
 * ✅ **THE GATE ITSELF NOW LIVES IN `@/lib/credit-notes` AND IS IMPORTED.** It
 * was declared here AND in `InvoiceDetailPage`, and both copies carried a note
 * saying they should not be two; they are one as of 2026-09-15.
 *
 * 🔴 **READ `creditNoteGateOf`'s OWN DOC BEFORE CHANGING ANYTHING ABOUT WHICH
 * INVOICES MAY BE CREDITED.** It enforces the owner's rule — any invoice that
 * has been ISSUED, and not a cancelled one — as the positive test
 * `isAwaitingPayment || isPaid`. That module records the truth table, why the
 * negative `!isDraft` is forbidden, and why `isSettled` is no part of it. **It
 * is not restated here, because a second copy of an ARGUMENT rots the same way
 * a second copy of a rule does.**
 *
 * ⚠️ **What stays out of `@/lib/credit-notes` is the FETCH** — it decides and
 * never fetches, which is its own standing rule. The status-master read
 * (`useInvoiceStatusFlags`) and the control (`RaiseCreditNoteButton`) moved
 * verbatim to `@/components/credit-notes/` on 2026-10-12, so the company page's
 * "Sales & billing history" mounts the same ones.
 * --------------------------------------------------------------------- */

/**
 * One body cell's content, keyed off the same column list the header renders
 * from — so a column cannot appear in one and not the other.
 *
 * ⚠️ **One badge in Status, not two, since 2026-09-02** — the "From Xero" chip
 * that used to sit beside it has a column of its own now. The wrapping `div`
 * went with it: it existed to lay two badges out, and a flex row around a single
 * child is the leftover that makes a reader look for the missing sibling.
 *
 * ⚠️ **From Xero is the WORD, not a badge.** The owner asked for *"a new column
 * with yes no"*, and a column of identical chips reads as decoration where a
 * column of two words reads as data you can scan. **"No" is NOT "not in Xero"** —
 * see `src/lib/imported-from-xero.ts`; nothing here may say so on screen, and the
 * label deliberately stays the owner's two words rather than the more precise
 * "Imported" / "Created here".
 */
function InvoiceRowCellContent({
  columnKey,
  invoice,
  statuses,
  canUpdate,
  canDelete,
  creditGate,
  creditChecking,
  onEdit,
  onDelete,
  onRaiseCreditNote,
}: {
  columnKey: InvoiceColumnKey;
  invoice: Invoice;
  statuses: readonly InvoiceStatus[];
  canUpdate: boolean;
  canDelete: boolean;
  /** Whether a credit note may be raised from this invoice — see `creditNoteGateOf`. */
  creditGate: CreditNoteGate;
  /** This row's already-credited lookup is in flight. */
  creditChecking: boolean;
  onEdit: (invoice: Invoice) => void;
  onDelete: (invoice: Invoice) => void;
  onRaiseCreditNote: (invoice: Invoice) => void;
}) {
  switch (columnKey) {
    case SUBJECT_COLUMN:
      return <SubjectCell invoice={invoice} />;
    case STATUS_COLUMN:
      return <StatusBadge status={invoice.status} statuses={statuses} />;
    case BILL_DATE_COLUMN:
      return <>{formatDateOnly(invoice.billDate)}</>;
    // Four states, never two — see LineProjectStatusCell. The value is the
    // SERVER's, derived from the line items; it is not computed from
    // `invoice.project`, which is the header project and answers a different
    // question.
    case LINE_PROJECT_COLUMN:
      return <LineProjectStatusCell status={invoice.lineProjectStatus} />;
    case IMPORTED_FROM_XERO_COLUMN:
      return <>{importedFromXeroLabel(invoice.importedFromXeroAt)}</>;
    case SENT_TO_CLIENT_COLUMN:
      return <>{sentCellText(invoice)}</>;
    case DUE_DATE_COLUMN:
      return <DueDateCell invoice={invoice} statuses={statuses} />;
    case GRAND_TOTAL_COLUMN:
      return <InvoiceMoney value={invoice.grandTotal} currency={invoice.currency} />;
    case ACTIONS_COLUMN:
      return (
        <div className="flex justify-end gap-1.5">
          {/* Ungated — the PDF route carries no role guard, so this is offered
              to everyone the row is offered to, a `viewer` included. It renders
              nothing unless the invoice is in Xero. */}
          <DownloadPdfButton
            kind="invoice"
            document={invoice}
            reference={invoice.subject}
            variant="row"
          />
          {/*
            ⚠️ **GATED ON `credit-notes`, NOT ON `invoices`** — the permission
            travels inside `creditGate`, which tests it BEFORE the paid state
            so a refused reader is told once rather than meeting a per-row
            sentence about a fact they could not act on.

            ⚠️ **DISABLED RATHER THAN HIDDEN, even for a refusal**, which is
            the opposite of Edit and Delete beside it. Those are gated on
            `invoices`, the module this whole page is about, so their absence
            is legible. A credit note is the one thing here raised from ANOTHER
            module, and a control that silently never appears is
            indistinguishable from a feature that was never built — the
            `/companies` "+" incident, where the owner reported a merely
            unreachable menu as DELETED.
          */}
          <RaiseCreditNoteButton
            invoice={invoice}
            gate={creditGate}
            variant="row"
            busy={creditChecking}
            onRaise={onRaiseCreditNote}
          />
          {canUpdate && (
            <Button
              variant="ghost"
              size="sm"
              iconOnly
              leadingIcon={<PencilIcon />}
              aria-label={`Edit ${invoice.subject}`}
              title={`Edit ${invoice.subject}`}
              onClick={() => onEdit(invoice)}
            />
          )}
          {canDelete && (
            <Button
              variant="danger"
              size="sm"
              iconOnly
              leadingIcon={<TrashIcon />}
              aria-label={`Delete ${invoice.subject}`}
              title={`Delete ${invoice.subject}`}
              onClick={() => onDelete(invoice)}
            />
          )}
          {/*
            ✅ **THE "no actions here" PLACEHOLDER IS GONE, and its own rule is
            what removed it.** *SUPERSEDED: `{!canUpdate && !canDelete &&
            !canDownloadPdf(invoice) && <span>—</span>}`, whose comment read
            "it must account for the PDF control or it renders an em dash
            BESIDE a live button — a cell claiming to be empty while holding
            something".*

            The credit-note control above renders on EVERY row — enabled where
            the gate allows it, disabled with its reason otherwise — so this
            cell is never empty and the condition can no longer be true. Left
            in place it would be a dead branch that reads as a live rule, and
            the day somebody made the control conditional it would put the em
            dash back beside a disabled button, which is the exact defect the
            superseded comment was written about.
          */}
        </div>
      );
  }
}

function ListSkeleton() {
  return (
    <ul className="m-0 list-none p-0">
      {Array.from({ length: SKELETON_ROWS }, (_, i) => (
        <li
          key={i}
          className="flex items-start justify-between gap-3 border-b border-border px-4 py-3 last:border-b-0"
        >
          <div className="grid flex-1 gap-1.5">
            <Skeleton className="h-3 w-44" />
            <Skeleton className="h-2.5 w-24" />
            <Skeleton className="h-2.5 w-36" />
          </div>
          <Skeleton className="h-4 w-20" />
        </li>
      ))}
    </ul>
  );
}

/**
 * The bill-date window, as ONE control rather than two loose boxes.
 *
 * A `<fieldset>`/`<legend>`, matching `Starts between` on `/projects` and the
 * closing-date range on `/deals`: it reads as one question, and a screen reader
 * announces "Billed between, From". The per-input labels stay VISIBLE — a date
 * box with nothing on it cannot say which end of the range it is, and a label is
 * not helper text.
 *
 * Both values arrive already validated by `dateBound`, so `from` and `to` are
 * either '' or a real `YYYY-MM-DD`. The inverted-range test is therefore a plain
 * STRING comparison with no `Date` anywhere near it.
 *
 * ⚠️ The inverted-range message is an ERROR, not advice. The server accepts a
 * back-to-front pair and simply returns nothing, which on this page is
 * indistinguishable from "nothing was billed in that window" — the bounds are
 * still applied and still in the URL; the sentence only explains the empty
 * result.
 */
function BillDateRangeFilter({
  from,
  to,
  onFrom,
  onTo,
}: {
  from: string;
  to: string;
  onFrom: (value: string) => void;
  onTo: (value: string) => void;
}) {
  const inverted = from !== '' && to !== '' && from > to;

  return (
    <fieldset className="m-0 min-w-0 border-0 p-0">
      <legend className="mb-1.5 p-0 text-xs font-medium text-fg-secondary">Billed between</legend>
      {/* Wraps onto two lines at 320px, side by side from `sm`. */}
      <div className="flex min-w-0 flex-wrap items-start gap-2">
        <div className="min-w-0 flex-1 basis-36">
          <Field label="From">
            {({ id, 'aria-describedby': describedBy }) => (
              <Input
                id={id}
                type="date"
                aria-describedby={describedBy}
                value={from}
                onChange={(e) => onFrom(e.target.value)}
              />
            )}
          </Field>
        </div>
        <div className="min-w-0 flex-1 basis-36">
          <Field label="To" {...(inverted ? { error: 'Before the “from” date.' } : {})}>
            {({ id, 'aria-describedby': describedBy }) => (
              <Input
                id={id}
                type="date"
                aria-describedby={describedBy}
                invalid={inverted}
                /*
                 * No `min`/`max`: an invoice is billed in the past as readily as
                 * the future, and a native bound would make the inverted pair
                 * silently unreachable rather than explained.
                 */
                value={to}
                onChange={(e) => onTo(e.target.value)}
              />
            )}
          </Field>
        </div>
      </div>
    </fieldset>
  );
}

/**
 * Two buttons, not a Select: this switches the view, it does not filter, and
 * `aria-pressed` states which one is on without a listbox to open. It sits in
 * `PageHeader`'s actions, which wraps — `PanelHeader`'s does not, and a control
 * that cannot wrap next to a bank of filters is how a page blows out sideways.
 */
function ViewToggle({
  view,
  onChange,
}: {
  view: InvoicesView;
  onChange: (next: InvoicesView) => void;
}) {
  return (
    <SharedViewToggle
      label="Invoice view"
      value={view}
      options={[...VIEW_OPTIONS]}
      onChange={onChange}
    />
  );
}

export function InvoicesPage() {
  /*
   * `ability`, never `user.role`: the role decides and `/permissions/me` may
   * only narrow it, and the reconciliation for that lives in permissions.ts.
   * Create and edit are WRITE_ROLES on this resource — `basic_user` and
   * `advanced_user` included — while delete is admin in every module.
   */
  const { ability, user } = useAuth();
  /*
   * ⚠️ **No `useNavigate` here any more.** The one caller was the credit-note
   * flow, and the navigation belongs with the two-phase write that decides
   * WHEN it is safe: a raised note lands on its own page only once its LINES
   * have copied, because navigating on create would leave a failed copy with
   * nowhere to report itself. `useRaiseCreditNote` owns both halves.
   */

  /*
   * The read gate. A hidden sidebar row is an optimisation and never a
   * guarantee — the URL is typeable, the command palette offers the page, and a
   * permission set can change under an access token that lasts days — so the
   * page answers for itself.
   *
   * ⚠️ DEGRADES OPEN: only an explicit `none` scope denies. See `can()` in
   * `@/lib/permissions`, which carries the reasoning.
   *
   * ⚠️ **Hiding a page is not refusing a request.** `GET /invoices` answers the
   * same to everybody, so nothing here may be worded as though the data were
   * protected. This is a view control.
   *
   * ⚠️ Computed here rather than as an early return, because the return has to
   * sit below every hook. The write gates described above are untouched.
   */
  const canReadModule = ability.canRead('invoices');

  const [searchParams, setSearchParams] = useSearchParams();

  /*
   * Saved filters, against the EMERGE account rather than this browser.
   *
   * ⚠️ **The return value is READ now, and that is not decoration.** It is the
   * sequencing signal for the Account lead default below: two writers put
   * filters into this URL on arrival, and the default has to wait for this one
   * and stand down when it applied anything — including when it deliberately
   * applied no lead. See `useDefaultAccountLeadFilter`.
   */
  const applySavedFilters = useSavedFilterApply<SavedInvoicesKey>();
  const savedFilters = useSavedFilters({
    scope: 'invoices',
    keys: SAVED_FILTER_KEYS,
    independentKeys: SAVED_VIEW_KEYS,
    store: preferencesStore,
    apply: applySavedFilters,
  });

  const [result, setResult] = useState<Paginated<Invoice> | null>(null);
  const [search, setSearch] = useRememberedState('search', '');
  const [page, setPage] = useState(1);
  const [error, setError] = useState<string | null>(null);
  const [loading, setLoading] = useState(true);
  /** "Show all" — remembered against the account. See `load`. */
  const [showAll, setShowAll] = useShowAll('invoices');
  /** The show-all read stopped at its cap; the pager says so. */
  const [showAllTruncated, setShowAllTruncated] = useState(false);

  const [formState, setFormState] = useState<{ invoice?: Invoice } | null>(null);
  /**
   * Bumped on every successful save so the board refetches; see `handleSaved`.
   * The board compares it with the value it last saw, so the number itself is
   * never read and never has to mean anything.
   */
  const [boardRefresh, setBoardRefresh] = useState(0);
  /*
   * ⚠️ **`xeroReport` and `listRefresh` were declared here and went with the Xero
   * column on 2026-08-31.** Both existed only to serve it: the first held the
   * push report the banner rendered, the second was bumped by `handleXeroSettled`
   * so the list re-read the row a push had just changed.
   *
   * `listRefresh` had exactly ONE writer — that handler — so with the column gone
   * it could only ever have stayed 0. It is removed rather than left as a token
   * nothing bumps; `noUnusedLocals` would have refused the unused setter anyway.
   * ⚠️ `/quotes` KEEPS its `listRefresh`, which is not an inconsistency: a second
   * writer there (the signature path) still bumps it.
   */
  const [pendingDelete, setPendingDelete] = useState<Invoice | null>(null);
  /*
   * Stamped with the invoice it was counted for. The count is fetched without
   * an abort, so opening a dialog, cancelling and opening another quickly can
   * land the first response after the second dialog is up — and a confirmation
   * naming a number that is not true is worse than one naming none. The stamp
   * is compared at render, the same id-stamped read the detail page uses.
   */
  const [pendingLineCount, setPendingLineCount] = useState<{ id: number; count: number } | null>(
    null,
  );
  const [deleting, setDeleting] = useState(false);
  const [deleteError, setDeleteError] = useState<string | null>(null);

  /*
   * The serialised query is the memo/effect dependency, never the parsed object:
   * `readFilters(...)` returns a fresh object on every render, so keying the
   * fetch on it would refetch forever. A string compares by value.
   */
  const queryString = searchParams.toString();

  // Junk is the table, not a 404. `view` is never forwarded to the API — it is
  // not in ID_FILTERS and the list request is built key by key.
  const view: InvoicesView = searchParams.get('view') === 'board' ? 'board' : 'table';

  const setView = useCallback(
    (next: InvoicesView) => {
      const params = new URLSearchParams(queryString);
      // The table is the default, so it is spelled by the key's absence rather
      // than by `?view=table` — one canonical URL per view. This has to flip
      // with the default above, or the page writes `?view=table` for ever.
      if (next === 'board') params.set('view', 'board');
      else params.delete('view');
      setSearchParams(params, { replace: true });
    },
    [queryString, setSearchParams],
  );

  const filters = useMemo(() => {
    const current = new URLSearchParams(queryString);
    const ids = {} as Record<IdFilter, number | null>;
    for (const key of ID_FILTERS) ids[key] = parseId(current.get(key));
    /*
     * The multi-id filters, parsed the same way and from the same string — a
     * `number[]`, empty for "no filter". It is spread AFTER `ids` so the two
     * families cannot silently overlap: a key in both lists would show up here
     * as a type error rather than as one shape quietly winning.
     */
    const idLists = {} as Record<IdListFilterKey, number[]>;
    for (const key of ID_LIST_FILTERS) idLists[key] = parseIdList(current.get(key));
    const dates = {} as Record<DateFilter, string>;
    for (const key of DATE_FILTERS) dates[key] = dateBound(current.get(key));
    return { ...ids, ...idLists, ...dates, currency: parseCurrency(current.get('currency')) };
  }, [queryString]);

  /*
   * The table hook owns the sort, so the headers and the mobile filter sheet
   * drive ONE state rather than two that can disagree. It never reorders rows
   * itself — `sort` is a query, and reordering one loaded page while implying
   * the whole table moved is worse than no sort at all.
   *
   * `rows` is memoised on `result` because the hook takes it as a dependency;
   * `?? []` alone is a fresh array every render.
   */
  const rows = useMemo(() => result?.data ?? [], [result]);
  /*
   * ⚠️ **The status master is read HERE, above the table hook, because the
   * Status funnel's option list is that master.** It used to be fetched further
   * down beside the currencies; only the position moved, and it has to — a hook
   * cannot be called after the one that consumes it. `useInvoiceStatusOptions`
   * fetches WITHOUT `isActive`, deliberately, so a retired status still resolves
   * on a record that holds one; the create/edit narrowing is `selectableStatuses`
   * at render.
   */
  const statuses = useInvoiceStatusOptions();
  /**
   * The status vocabulary BOTH status controls render — the "All statuses"
   * Combobox in the filter bank and the Status funnel on the header.
   *
   * 🔴 **RETIRED STATUSES ARE OFFERED, AND THAT IS THE OPPOSITE OF WHAT A CREATE
   * PICKER DOES.** *SUPERSEDED: `selectableStatuses(statuses.statuses, null)`,
   * whose own note read "offering a retired status here would be offering a
   * filter that matches the rows nobody is looking for."* **That is exactly
   * backwards.** `selectableStatuses` narrows to `isActive` because a retired
   * status is not a value a NEW record should take — but reads never narrow on
   * `isActive`, so a live invoice can and does hold one. Hiding it makes those
   * invoices unreachable through the only control that can reach them, and the
   * popover gives no hint that anything is missing: a filter that cannot find
   * rows that exist, which is the defect `ColumnSpec.options` was written for
   * wearing a different disguise. `/companies`' Region funnel took the same
   * decision against the same helper family.
   *
   * ⚠️ **The `(retired)` suffix repeats `selectableStatuses`' wording rather
   * than reusing it**, because that helper FILTERS as well as labels and the
   * filtering is precisely what must not happen here. One word duplicated is
   * cheaper than a shared helper that has to be told which of its two jobs to do.
   *
   * ⚠️ **The id in the URL is injected when the master does not carry it.** A
   * deep link into a status the master no longer returns still gets a ticked
   * checkbox that can be UNticked, rather than sticking as filtered with nothing
   * to clear. `#N` is the honest label — the name is not in hand, and inventing
   * one would be a claim about a row we did not read. Same idiom as
   * `currencyOptions`' pinned-back code and `/companies`' `withSelectedValues`.
   *
   * Master order is the curated picker order (`sortOrder`, then `name`) and is
   * used as received — `use-invoice-options` says not to re-sort it.
   */
  const statusOptions = useMemo(() => {
    const options = statuses.statuses.map((status) => ({
      value: String(status.id),
      label: status.isActive ? status.name : `${status.name} (retired)`,
    }));
    // Every id the URL names, not just one: the filter takes several now.
    for (const id of [...filters.statusId].reverse()) {
      const chosen = String(id);
      if (!options.some((option) => option.value === chosen)) {
        options.unshift({ value: chosen, label: `#${chosen}` });
      }
    }
    return options;
  }, [statuses.statuses, filters.statusId]);
  /*
   * The SAME list the "All statuses" Combobox renders, narrowed to the two keys
   * `ColumnFilterOption` declares.
   *
   * ⚠️ **Derived from `statusOptions` rather than from `statuses.statuses`
   * again.** Two controls that write one query key must not offer two different
   * vocabularies: a status the funnel lists and the Select does not (or the
   * reverse) is a filter that appears and disappears depending on which control
   * you reach for, with nothing failing. Mapping the one list is what keeps the
   * retired-status rule — and any future change to it — in a single place.
   *
   * 🔴 **`undefined` WHEN THE MASTER HAS RETURNED NOTHING**, which covers a
   * failed read and the first paint alike. A declared list is a CLAIM that the
   * vocabulary is closed and known; a list built from a request that failed is
   * the same claim with nothing behind it, and it renders as an empty popover
   * that reads like an answer. See `invoiceColumns`. The injected URL id is not
   * enough to make the list a master read, so it is tested on `statuses.statuses`
   * and not on the length of `statusOptions`.
   */
  const statusFilterOptions = useMemo<ColumnFilterOption[] | undefined>(() => {
    if (statuses.statuses.length === 0) return undefined;
    return statusOptions.map((option) => ({ value: option.value, label: option.label }));
  }, [statusOptions, statuses.statuses]);
  /*
   * Memoised because `useTableFilters` takes `columns` as a dependency of its
   * value-accumulating effect and of two memos; a fresh array every render costs
   * an extra settling pass on each one. `invoiceColumns` is pure.
   */
  const columns = useMemo(() => invoiceColumns(statusFilterOptions), [statusFilterOptions]);
  const table = useTableFilters(rows, columns);
  const visibleRows = table.visibleRows;
  const sort = table.sort;

  /*
   * ── The reader's own column arrangement ─────────────────────────────────────
   *
   * Order, hidden set and the two frozen edges, stored against the ACCOUNT under
   * `preferences.columns.invoices` — not `localStorage` (per browser, per device,
   * gone with site data) and not the URL (a shared link describes WHICH ROWS; the
   * sender's hidden columns are not the recipient's business). The hook's header
   * carries the long version of both.
   *
   * ⚠️ **A FAILED READ DEGRADES TO THE DEFAULT LAYOUT AND NEVER BLOCKS THE
   * TABLE** — the rows are the page, the arrangement is chrome. `loading` is not
   * read here for the same reason: the default order renders immediately and is
   * replaced when the preference arrives.
   */
  const layout = useColumnLayout(
    'invoices',
    COLUMN_KEYS,
    LOCKED_COLUMN_KEYS,
    PINNED_COLUMN_KEYS,
  );
  /*
   * ⚠️ **DESTRUCTURED SO THE MEMOS BELOW DEPEND ON THESE TWO AND NOT ON
   * `layout`.** The hook returns a fresh object literal every render while these
   * two members are stable `useMemo`/`useCallback` results, so depending on the
   * object would rebuild `renderedColumns` on every render — and, worse, hand
   * `useFrozenOffsets` a new `frozenSideOf` each time, which re-runs its layout
   * effect and re-attaches its `ResizeObserver` on every render. Destructuring is
   * also what lets the exhaustive-deps rule see them, rather than leaving two
   * suppressed warnings for somebody to "fix" by adding `layout`.
   */
  const { frozenSideOf, visibleKeys } = layout;

  /**
   * The columns actually rendered, in order, **with Actions forced last**.
   *
   * 🔴 **TWO THINGS THIS GUARANTEES THAT THE LAYOUT ALONE DOES NOT.**
   *
   * - **Actions is always present.** `useColumnLayout` refuses to HIDE a locked
   *   key, but it does not exempt one from a `hidden` array already stored — so a
   *   layout written before Actions was locked (or by a future column set) could
   *   name it, and `toggleVisible` would then refuse to put it back. That is a
   *   reader with no Edit, no Delete and no PDF and no way to recover them short
   *   of Reset. Appending it unconditionally makes that state unreachable.
   * - **Actions is always LAST**, which is what `pinned` promises. `pinned` is
   *   `sticky right-0`; the manager's drag-and-drop can drop another column past
   *   a locked one (`moveTo` accepts the end index), and a column rendered after
   *   a right-pinned cell scrolls underneath it.
   */
  const columnKeys = useMemo<readonly InvoiceColumnKey[]>(() => {
    const ordered = visibleKeys.filter(
      (key): key is InvoiceColumnKey => key !== ACTIONS_COLUMN && key in COLUMNS,
    );
    return [...ordered, ACTIONS_COLUMN];
  }, [visibleKeys]);

  /*
   * ✅ **THE HEADER ROW CARRIES THE REF ITSELF — `TRProps` TAKES ONE NOW.**
   * *SUPERSEDED: a `useState` holding the row, a `useCallback` ref on a wrapper
   * `<div>` that found it with `querySelector('thead > tr')`, and a
   * `useMemo`-built ref object — all because "`TRProps extends HTMLAttributes`,
   * which carries no `ref`, and `Table.tsx` is shared and not this change's to
   * edit".* The wrapper and the selector are gone; a plain `useRef` on the `TR`
   * is the whole mechanism.
   *
   * ⚠️ **A PLAIN `useRef` IS SAFE NOW AND WAS NOT BEFORE, AND THE REASON IS IN
   * THE HOOK, NOT HERE.** The old note warned that a stable ref object would be
   * measured once and never again, because the hook's effect keyed off its
   * dependencies and a swapped-in row element is not a value React can be told
   * to watch. **`useFrozenOffsets` tracks the observed ELEMENT itself now** — it
   * compares the node on every render and re-measures when it changes — so the
   * identity churn this page was manufacturing is no longer what drives it.
   */
  const headRowRef = useRef<HTMLTableRowElement | null>(null);

  /*
   * ⚠️ **Measured, not declared.** This table has no `table-fixed` and no
   * `<colgroup>`, so a `w-*` on a `<th>` is a request the content can outvote and
   * the rendered width of a column is knowable only from the DOM. The array here
   * MUST be the same one the header maps over, in the same order — the hook pairs
   * `columnKeys[i]` with the header row's i-th cell.
   *
   * ✅ **ACTIONS IS DECLARED AS A PINNED KEY RATHER THAN FAKED AS RIGHT-FROZEN.**
   * *SUPERSEDED: a local `frozenSideForOffsets` callback answering `'right'` for
   * `ACTIONS_COLUMN` before delegating to `frozenSideOf`.* The fact it was
   * asserting is unchanged and still load-bearing: **a right-frozen column's
   * offset is the summed width of the right-frozen columns OUTBOARD of it**, so
   * leaving the pinned lane out of that sum places the next right-frozen column
   * at `right: 0` — directly underneath it, a real overlap that reads as the
   * column simply failing to stick. `useFrozenOffsets` folds `pinnedKeys` in
   * itself now, so the page states the fact once and no wrapper re-derives it.
   */
  const frozenOffsets = useFrozenOffsets(
    headRowRef,
    columnKeys,
    frozenSideOf,
    PINNED_COLUMN_KEYS,
  );

  const renderedColumns = useMemo<readonly RenderedColumn[]>(
    () =>
      columnKeys.map((key) => {
        // Actions keeps `pinned` — the one-column special case of `frozen="right"`
        // with a zero offset — rather than being migrated to the general form.
        if (key === ACTIONS_COLUMN) return { key, pinned: true };
        const frozen = frozenSideOf(key);
        return frozen === undefined ? { key } : { key, frozen, frozenOffset: frozenOffsets[key] };
      }),
    [columnKeys, frozenSideOf, frozenOffsets],
  );

  const tableWidth = useMemo(() => tableMinWidth(columnKeys), [columnKeys]);

  /*
   * The params the hook's server-scoped columns emit, typed against the list
   * contract. *SUPERSEDED 2026-09-02: "The ONE server-scoped column's param."*
   * There are **two** — Projects and From Xero.
   *
   * ⚠️ **STATUS IS NOT AMONG THEM AND MUST NEVER BE.** Its column declares no
   * `param`, so the hook emits nothing for it; the selection lives in the URL
   * where the "All statuses" Combobox already writes it, and `load()` picks it
   * up out of `ID_FILTERS` like every other single-id filter. Adding `param:
   * 'statusId'` here would put a second `statusId` into the same request.
   *
   * ⚠️ **RE-PARSED RATHER THAN TRUSTED.** `serverParams` is
   * `Record<string, string>`, so without the membership check a stale value
   * would reach a `.strict()` enum and 422 the whole list — which renders as an
   * empty table, not as a bad filter.
   *
   * ✅ **Projects is `multi` since 2026-10-28** and arrives as a CSV of states.
   * From Xero and Sent to client stay single: they are booleans, and ticking
   * both halves of a partition is no filter at all, so the hook's page-scoped
   * fallback for several ticks is the honest behaviour there.
   *
   * `serverParams` keeps a stable identity while unchanged, so this memo and the
   * fetch depending on it settle rather than loop. Same shape as `/quotes`.
   */
  const { serverParams } = table;
  const listFilters = useMemo<
    Pick<InvoiceListParams, 'lineProjectStatus' | 'importedFromXero' | 'sentToClient'>
  >(
    () => {
      const next: Pick<
        InvoiceListParams,
        'lineProjectStatus' | 'importedFromXero' | 'sentToClient'
      > = {};

      /*
       * A CSV now (`multi`), so each MEMBER is checked against the declared
       * list and junk is dropped rather than sent to a strict enum.
       */
      const projectStatuses = (serverParams.lineProjectStatus ?? '')
        .split(',')
        .filter((value): value is LineProjectStatus =>
          LINE_PROJECT_FILTER_OPTIONS.some((option) => option.value === value),
        );
      if (projectStatuses.length > 0) next.lineProjectStatus = projectStatuses.join(',');

      /*
       * From Xero, on the same terms — re-parsed against the declared list, then
       * turned into the BOOLEAN the params type takes.
       *
       * ⚠️ **`false` IS A REAL FILTER HERE and must survive the trip.** The
       * column is a timestamp or `NULL` with no third state, so "No" partitions
       * the list rather than meaning "no filter" — `listQuery()` keeps a `false`
       * for exactly this, and `Object.keys(listFilters).length` below counts it
       * because it counts KEYS rather than testing truthiness. Its neighbours on
       * that schema (`unsettled`, `overdue`) do treat `false` as "no filter";
       * this one does not.
       *
       * ⚠️ **An unrecognised value is DROPPED rather than coerced.** A bare
       * `raw === 'true'` would quietly answer `false` for anything else — so a
       * vocabulary change in `imported-from-xero.ts` would silently invert half
       * this filter with nothing failing. Both encodings `queryBoolean()` accepts
       * are spelled out, and anything else falls through to "no filter", which is
       * the visible wrong answer rather than the silent one.
       */
      const fromXero = serverParams[IMPORTED_FROM_XERO_COLUMN];
      if (
        fromXero !== undefined &&
        IMPORTED_FROM_XERO_FILTER_OPTIONS.some((option) => option.value === fromXero)
      ) {
        if (fromXero === 'true' || fromXero === '1') next.importedFromXero = true;
        else if (fromXero === 'false' || fromXero === '0') next.importedFromXero = false;
      }

      // Sent to client, on exactly the From Xero terms above: `false` is a
      // real filter, and an unrecognised value is dropped rather than coerced.
      const sent = serverParams[SENT_TO_CLIENT_COLUMN];
      if (
        sent !== undefined &&
        SENT_TO_CLIENT_FILTER_OPTIONS.some((option) => option.value === sent)
      ) {
        if (sent === 'true' || sent === '1') next.sentToClient = true;
        else if (sent === 'false' || sent === '0') next.sentToClient = false;
      }

      return next;
    },
    [serverParams],
  );

  const load = useCallback(
    (signal?: AbortSignal) => {
      setLoading(true);
      const params: InvoiceListParams = { page, perPage: PER_PAGE };
      // Every key is written only when it has a value. An empty string is a 422
      // on this endpoint rather than "no filter", so `?statusId=` must never be
      // sent; `?search=` is the one harmless exception and is still omitted.
      if (search) params.search = search;
      const current = new URLSearchParams(queryString);
      for (const key of ID_FILTERS) {
        const value = parseId(current.get(key));
        if (value !== null) params[key] = value;
      }
      /*
       * The multi-id filters, sent as the comma-separated list the endpoint
       * takes: `?lineProjectId=4,9`, several ids meaning OR.
       *
       * ⛔ **AN EMPTY SELECTION OMITS THE KEY. `?lineProjectId=` is a 422 on
       * this `.strict()` schema, not "no filter"** — the same rule as
       * `?statusId=` above and `?billDateAfter=` below. `parseIdList` returns
       * `[]` for nothing selected, for a blank value and for junk alike, and
       * `length === 0` is the only test between it and the request. (`listQuery`
       * would drop an empty string as well; that is a second net, not the
       * reason this is safe.)
       *
       * ⚠️ **Re-parsed out of the query string rather than read off `filters`,
       * exactly as the loop above does** — `load` already keys on `queryString`,
       * and taking one of the two families from a memo would give the fetch two
       * sources for one request.
       */
      for (const key of ID_LIST_FILTERS) {
        const values = parseIdList(current.get(key));
        if (values.length > 0) params[key] = values.join(',');
      }
      const currency = parseCurrency(current.get('currency'));
      if (currency !== null) params.currency = currency;
      /*
       * The two bill-date bounds go out as the strings they are — no parsing, no
       * `Date`, no normalising. `dateBound` has already refused anything that is
       * not a real calendar date, so '' here means "no bound" and the key is
       * omitted; `billDateAfter=''` would be a 422, not an unfiltered list.
       */
      for (const key of DATE_FILTERS) {
        const value = dateBound(current.get(key));
        if (value) params[key] = value;
      }
      /*
       * The Projects funnel's server-scoped selection. Merged as an OBJECT that
       * is either empty or holds exactly one validated key, so there is no path
       * that sends `lineProjectStatus=''` — an empty string is a 422 on a strict
       * enum, not "no filter".
       *
       * ⚠️ It lives in the hook's state rather than in the URL, unlike every
       * filter above it here. So it is NOT shareable in a link. It IS remembered
       * on the account with the header sort (`useTableFilters` →
       * `useRememberedTableState`, owner 2026-10-16/10-23); *SUPERSEDED: "NOT
       * saved against the account, and it resets on reload"* — the same trade every
       * server-scoped funnel in this app already makes. Moving it into the URL
       * means making `useTableFilters` a controlled component, which is a change
       * to the primitive and to five other pages.
       */
      Object.assign(params, listFilters);
      /*
       * ⚠️ Sent ONLY when a header has actually been used. The endpoint's own
       * default is `id DESC`, and asking for it by name (`sort=id&order=desc`)
       * is the same ordering re-spelled as an instruction — which would pin this
       * page to today's default for ever, silently, the day the server's moves.
       * Nothing is the way to ask for the default.
       */
      if (sort) {
        params.sort = sort.key as InvoiceListSort;
        params.order = sort.direction;
      }

      /*
       * Show all: the same params, every page of them, 100 at a time. The page
       * number is ignored (and left in state untouched) while it is on, and
       * `fetchAllPages` throws on any failed page, so a short list is never
       * rendered as the whole one. A bare `load()` (after a save) has no signal
       * of its own, so it gets one nothing aborts — same as the paged path.
       */
      const request: Promise<Paginated<Invoice>> = showAll
        ? fetchAllPages<Invoice>(
            (pageNumber, perPage, pageSignal) =>
              api.listInvoices({ ...params, page: pageNumber, perPage }, pageSignal),
            signal ?? new AbortController().signal,
          ).then(({ rows: all, total: allTotal, truncated }) => {
            setShowAllTruncated(truncated);
            return {
              data: all,
              meta: { page: 1, perPage: all.length, total: allTotal, totalPages: 1 },
            };
          })
        : api.listInvoices(params, signal).then((data) => {
            setShowAllTruncated(false);
            return data;
          });

      return request
        .then((data) => {
          setResult(data);
          setError(null);
        })
        .catch((caught: unknown) => {
          if (signal?.aborted) return;
          setError(caught instanceof Error ? caught.message : 'Failed to load invoices');
        })
        .finally(() => {
          if (!signal?.aborted) setLoading(false);
        });
    },
    [page, search, queryString, sort, listFilters, showAll],
  );

  /*
   * Debounced so typing in the search box doesn't fire a request per keystroke;
   * the controller aborts the in-flight one when the term moves on.
   *
   * Board view skips the list request outright rather than loading rows nobody
   * can see. The board has its own fetch and its own debounce, and the limiter
   * is PER IP — shared by everyone behind the office NAT — so two requests per
   * keystroke for a table nobody can see is the wrong shape.
   *
   * ⚠️ *SUPERSEDED: "the limiter is 120 requests per 60 seconds PER IP … so two
   * requests per keystroke is the fastest way into it."* It is **12,000/60s**
   * (raised 2026-09-04, corrected 2026-09-17). ⛔ THE SHAPE IS THE OBJECTION,
   * NOT THE ARITHMETIC: doubling the request volume of a shared bucket to load
   * rows nobody can see is still wrong, but it is no longer imminent, so do not
   * re-derive this skip — or any of the per-row refusals below — from a budget
   * that is a hundred times what those sentences claimed.
   */
  useEffect(() => {
    if (view === 'board') return;
    const controller = new AbortController();
    const timer = setTimeout(() => void load(controller.signal), 250);
    return () => {
      clearTimeout(timer);
      controller.abort();
    };
    // `listRefresh` was a third dependency here — a re-run token bumped after a
    // Xero push — and went with that column on 2026-08-31.
  }, [load, view]);

  /*
   * ⚠️ **`statuses` and `statusOptions` WERE DECLARED HERE and moved up on
   * 2026-09-02**, above `useTableFilters` — the Status funnel's option list is
   * that master, and a hook cannot be called after the one that consumes it.
   * Nothing about the fetch changed; only its position.
   */
  const currencies = useCurrencyOptions();
  /*
   * The "All projects" filter's list — one `GET /projects?isActive=true` per
   * mount, alongside the statuses and currencies above.
   *
   * ⚠️ **Fetched UNCONDITIONALLY, board view included, and that is the cheaper
   * of the two mistakes.** The board cannot narrow by it (the notice below says
   * so) but the control renders in both views, exactly as "All statuses" does —
   * so gating the fetch on `view` would leave a reader switching to the board
   * with a Combobox that empties and refills on the way back. One request
   * against a 12,000/60s (⚠️ *not the 120/60s this once said*) bucket is a
   * smaller cost than a control that flickers.
   */
  const projects = useProjectOptions();
  /*
   * ── The Account lead filter starts on the SIGNED-IN PERSON ────────────────
   *
   * Owner's instruction, 2026-09-12: a bare `/invoices` shows the invoices
   * attached to whoever is looking. Same mechanism, same module and same
   * refusals as `/deals` — `useDefaultAccountLeadFilter` carries the reasoning.
   *
   * ⚠️ *SUPERSEDED: "**`useUserOptions()` was called here for the "All owners"
   * filter and the call went with it on 2026-08-31.** Nothing on this page reads
   * the staff directory now, so a page load makes one fewer request against a
   * 120/60s per-IP bucket shared across the office."* ⚠️ *That quotation is
   * left verbatim and its figure was wrong twice over: the bucket is
   * **12,000/60s** (corrected 2026-09-17).* **The saving is spent
   * again, and on purpose** — two requests, `/users` and `/user-groups`, are the
   * whole cost of the default, and they are the ONLY evidence that can say
   * whether this person may be named as an account lead at all. Seeding without
   * them would be guessing. It is gated on `canReadModule`, so a reader who
   * cannot see the page pays nothing.
   *
   * ⛔ **The directory is evidence, NOT options.** This page still renders no
   * staff control of any kind; `accountLeadId` is arrived at by link and shown
   * as a badge in the "Also filtered by" bank, cleared by the Clear button
   * beside it. **That badge is the whole affordance**, which is thinner than
   * `/deals`' funnel, and is the thing to revisit if this ever needs a control
   * of its own — not this call.
   *
   * ⚠️ **`setPage(1)` is this page's half of the bargain.** `page` is component
   * state rather than a URL key here, so the hook cannot reset it; a seed IS a
   * filter change, and the hook waits on a preferences read that can take
   * seconds, so a reader may already have paged onto a slice of a different
   * result set.
   *
   * ⚠️ **oxlint says NOTHING about this one, and that is an artefact of where
   * the call sits rather than a verdict on it.** `react(set-state-in-effect)`
   * fires on a `setPage` written straight into an effect and does not fire on
   * one passed as a callback. Measured 2026-09-12. **A reader who deletes this
   * because nothing complains removes the guard, not the noise.**
   */
  const staff = useUserOptions(canReadModule, true);
  useDefaultAccountLeadFilter({
    user,
    staff,
    savedFilters,
    enabled: canReadModule,
    onSeeded: () => setPage(1),
  });

  /*
   * The value is the three-letter CODE, not an id — an invoice stores
   * `currency` as CHAR(3) with no foreign key to the currency table.
   *
   * 🔴 **ACTIVE-ONLY IS A DELIBERATE DEPARTURE FROM THE RULE THE STATUS FUNNEL
   * FOLLOWS, AND THE SECOND ARGUMENT IS WHAT PAYS FOR IT.** Everywhere else on
   * this page a filter offers retired master rows too, because reads never
   * narrow on `isActive` and a live record can hold one. `useCurrencyOptions`
   * sends `isActive: true` and cannot be told otherwise from here — and that is
   * the right list anyway: **161 of the 164 currency rows were retired on
   * 2026-08-31**, so the unfiltered master is a 164-row checkbox list of codes
   * no live document uses, which is its own way of hiding the three that matter.
   *
   * The hole that leaves is one code: a document genuinely billed in a retired
   * currency. *SUPERSEDED: `null` as the second argument.* Passing the URL's own
   * code pins it back — `currencyOptions` prepends it labelled "No longer
   * offered" — so a deep link into a retired currency renders as itself and can
   * be cleared, rather than showing a raw unmatched value the control cannot
   * describe. It does NOT make such a currency discoverable from the list, which
   * is the accepted cost, stated rather than hidden.
   */
  const currencyPickerOptions = useMemo(
    () => currencyOptions(currencies.currencies, filters.currency),
    [currencies.currencies, filters.currency],
  );
  /*
   * `POST /invoices` and `PATCH /invoices/:id` both take WRITE_ROLES, so a
   * basic_user and an advanced_user get the create and edit controls — this
   * page used to hide them behind the admin tier, which was stricter than the
   * API. Delete is ADMIN_ROLES in every module without exception.
   */
  const canCreate = ability.canCreate('invoices');
  const canUpdate = ability.canUpdate('invoices');
  const canDelete = ability.canDelete('invoices');
  const createDenial = denialReason(ability, 'create', 'invoices');
  // `PATCH /invoices/:id/status` — the board's move endpoint — is WRITE_ROLES
  // too, so dragging a card is an update, not an admin act.
  const moveDenial = denialReason(ability, 'update', 'invoices');

  /*
   * Raising a credit note from an invoice. The status master is read ONCE for
   * the page — see `useInvoiceStatusFlags` — rather than per row: one request
   * covers every row at any page size, where a per-row lookup would be
   * `perPage` requests against the office's shared 12,000-per-60s (⚠️ *not the
   * 120 this once said*) budget — the SHAPE is the objection, not the total.
   */
  const creditStatuses = useInvoiceStatusFlags();
  /*
   * ⚠️ **`credit-notes`, NOT `invoices`.** Raising a credit note is a write to
   * another module, and the two grants come apart: `accounts` holds
   * `credit-notes.create` while `operations` does not. A borrowed
   * `canCreate('invoices')` would be wrong in both directions — the
   * `/integrations`-gated-on-`companies.update` mistake this app has already
   * paid for once.
   */
  const canCreateCreditNote = ability.canCreate('credit-notes');
  const creditDenial = denialReason(ability, 'create', 'credit-notes');

  /*
   * ✅ **THE WHOLE FLOW IS ONE MODULE NOW, AND THIS PAGE OWNS NONE OF IT.**
   * *SUPERSEDED: a `creditFor` / `creditPrecheck` / `creditChecking` /
   * `creditPartial` quartet, an `openCreditNote` and a `copyCreditNoteLines`
   * here, with a byte-for-byte twin in `InvoiceDetailPage` — which both copies
   * said in their own comments was the thing to fix.*
   *
   * ⚠️ **They had already drifted by the time they were merged**, which is the
   * argument rather than an aside: the detail page's partial dialog carried a
   * `tone` and a cancel label this one did not, so one surface reported a
   * created note in red and the other did not. The stronger of the two
   * survived the merge.
   *
   * ⚠️ **`checkingId` is an id and not a boolean, and that still matters here**
   * — this list mounts one control per row, so a shared flag would spin every
   * one of them.
   *
   * ⚠️ **The already-credited lookup still runs ON THE PRESS, not per row.**
   * `?sourceInvoiceId=` answers for ONE invoice, so warning every row in
   * advance would be one request per row against the office's shared
   * 12,000-per-60s (⚠️ *not the 120 this once said*) budget — the batching rule
   * this app applies to every list embed, and it is a rule about the shape
   * rather than about the total. A deliberate press is one invoice, so it is
   * one request.
   */
  const raiseCredit = useRaiseCreditNote();

  /*
   * `?new=1` opens the create form, and `?companyId=N` beside it pre-fills the
   * company. That is what raising an invoice FOR a company means, as opposed to
   * `/invoices?companyId=N` on its own, which only ever filtered this list to
   * one — the gap the "+" menu on the companies list was falling into.
   *
   * A URL is not authorisation: without `canCreate` the parameter does nothing
   * at all, which is the same answer the New invoice button gives. `new=1` with
   * no `companyId` is a plain create form, not an error — the company is simply
   * not locked.
   */
  const wantsCreate = canCreate && searchParams.get('new') === '1';

  /*
   * Derived during render rather than pushed into `formState` by an effect. An
   * effect would open the form one render after the page painted without it,
   * and would then have to be stopped from reopening the form the user just
   * dismissed — whereas here dismissing it removes the parameter, and the
   * parameter IS the open state. `formState` still wins, so a row being edited
   * is never swapped for a create form.
   */
  const urlCreate = wantsCreate && formState === null;

  /*
   * Closing takes `new` back out of the URL, so a refresh or a Back press does
   * not reopen the form over a list the user meant to be looking at.
   * `replace: true` for the same reason every other write on this page uses it:
   * dismissing a dialog is not a navigation, and stacking a history entry for
   * it makes Back mean "reopen the thing I just closed".
   */
  const closeForm = useCallback(() => {
    setFormState(null);
    const next = new URLSearchParams(queryString);
    if (!next.has('new')) return;
    next.delete('new');
    setSearchParams(next, { replace: true });
  }, [queryString, setSearchParams]);

  /*
   * The company the create form is locked to, and the name to show for it.
   *
   * This page only ever knows the ID — the filter is read out of the URL and
   * /invoices is never asked about the company itself — which is exactly why
   * the "Also filtered by" badge reads `Company #22`. The locked field uses the
   * same identifier, sharpened to the real name whenever a loaded row happens
   * to carry it: an invoice already billed to that company brings its
   * `{ id, name }` along, so the name costs nothing when it is there and no
   * extra request when it is not. Fetching /companies/:id purely for a label
   * would be a second request against a 12,000-per-60s (⚠️ *not the 120 this
   * once said*) limiter, for a field nobody can edit — and it would leave the
   * field blank while it was in flight, which is the reason that survives.
   *
   * Memoised on `result` rather than on `rows`, which is a fresh array every
   * render and would hand the modal a new object each time.
   */
  /*
   * A loaded row is the free source, but it is empty in the case that matters
   * most: the "+" menu on a company that has no invoices yet. That is the whole
   * reason someone reaches for it. So a row's name is used when it happens to
   * be there, and otherwise the one company is fetched — matching what
   * DealsPage and QuotesPage do, so the three do not disagree about the same
   * company. Never a request when a row already answered it.
   */
  const namedFromRows =
    filters.companyId === null
      ? null
      : (result?.data.find((row) => row.company?.id === filters.companyId)?.company?.name ?? null);

  const [fetchedCompany, setFetchedCompany] = useState<{ id: number; name: string } | null>(null);
  const lockedCompanyId = urlCreate ? filters.companyId : null;

  useEffect(() => {
    if (lockedCompanyId === null || namedFromRows !== null) return;
    const controller = new AbortController();
    api
      .getCompany(lockedCompanyId, controller.signal)
      .then((envelope) => {
        if (controller.signal.aborted) return;
        setFetchedCompany({ id: lockedCompanyId, name: envelope.data.name });
      })
      .catch(() => {
        /*
         * Not an error state. The id still identifies the company and the label
         * falls back to it below, so the field is never blank — a failed label
         * lookup must not block creating an invoice.
         */
      });
    return () => controller.abort();
  }, [lockedCompanyId, namedFromRows]);

  const lockedCompany = useMemo(() => {
    if (!urlCreate || filters.companyId === null) return undefined;
    /* Only while it is THIS company's name — a stale one must never paint. */
    const fetched =
      fetchedCompany !== null && fetchedCompany.id === filters.companyId ? fetchedCompany.name : null;
    return {
      id: filters.companyId,
      name: namedFromRows ?? fetched ?? `Company #${filters.companyId}`,
    };
  }, [urlCreate, filters.companyId, namedFromRows, fetchedCompany]);

  /*
   * ── The page reset, which BOTH functions below share ────────────────────────
   *
   * ⚠️ **This block describes `headerFor`, which is now the SECOND function
   * under it** — `bridgeStatusToUrl` was added between the two on 2026-09-02.
   * Left where it was rather than moved, because everything in it is true of the
   * pair; the heading above says so instead of letting a comment quietly come to
   * sit over the wrong subject.
   *
   * The hook's headers, with the page reset folded into the event that changes
   * the query, so no request fires for a page that no longer exists — sorting a
   * table while standing on page 7 asks for page 7 of a different sequence.
   *
   * ⚠️ **THE SORT AND A SERVER-SCOPED FUNNEL RESET IT; A PAGE-SCOPED ONE DOES
   * NOT.** *SUPERSEDED 2026-09-02: "Only the SORT resets it. The Bill date
   * funnel's value and emptiness filters are page-scoped … so resetting the
   * pager for them would throw the reader off the page they were reading and
   * change nothing about what came back."* Both halves of that are still true OF
   * BILL DATE — it is still page-scoped and still must not reset — but it is
   * long since the only funnel here. Projects and From Xero are both
   * `scope: 'server'` and NARROW THE WHOLE LIST, so a stale page 7 under either
   * asks for page 7 of a much shorter result and renders as an empty book.
   *
   * ⚠️ The branch tests `filter.scope`, not the column key, so a future column
   * gaining a scope is covered without anybody remembering this comment.
   *
   * ⚠️ **STATUS IS THE ONE EXCEPTION, and it is not a special case bolted on** —
   * it brings its own reset with it, because `setFilter` already calls
   * `setPage(1)` for the selection while its EMPTINESS half is page-scoped and
   * must not reset at all. Running it through the generic wrapper as well would
   * reset the pager on an emptiness tick, which changes nothing about what comes
   * back. `headerFor` therefore picks one path or the other rather than both.
   *
   * Not memoised: `filters.headerFor` is a fresh function each render (the hook
   * says so at its declaration), so a `useCallback` here would hold nothing.
   */

  /*
   * ── The Status funnel is a VIEW OVER `?statusId=`, not a filter of its own ──
   *
   * 🔴 **THIS IS WHAT MAKES THE FUNNEL AND THE "All statuses" COMBOBOX ONE
   * FILTER.** Both controls answer the same question, so leaving each with its
   * own store would let them hold different statuses at once — and the visible
   * result is an empty table with two controls disagreeing and nothing on screen
   * explaining it. The URL is the single source of truth; this function makes the
   * funnel read and write it.
   *
   * That is the OPPOSITE call from Bill date, whose funnel is deliberately
   * independent of the bill-date range beside it — because those two ask
   * DIFFERENT questions of one column (exact values vs a window) and compose,
   * where two status pickers only contradict.
   *
   * Three things it has to get right:
   *
   * - **The SELECTION comes from and goes to the URL.** `setFilter` already
   *   resets the pager and writes `replace: true`, so a funnel press behaves
   *   exactly like the Combobox press it shares a key with.
   * - **The EMPTINESS toggle stays in the hook**, because no endpoint here
   *   exposes an is-null filter and `status_id` really is nullable. It is
   *   page-scoped, `useTableFilters` applies it before the server-column early
   *   return, and `table.activeCount` counts it — which is correct, since that
   *   count is the page-scoped one. The hook is handed `selected: []` every time
   *   so it can never accumulate a status of its own to disagree with the URL.
   * - ✅ **SEVERAL TICKED ARE ALL KEPT (2026-10-28).** *SUPERSEDED: "SEVERAL
   *   TICKED COLLAPSES TO ONE, because `?statusId=1,2` IS A 422".* Every ticked
   *   id is validated and written to the URL as one CSV, which the list and the
   *   invoices board both OR. The column still declares no `param`, so the URL
   *   stays the only place the selection lives.
   */
  function bridgeStatusToUrl(filter: ColumnFilterProps): ColumnFilterProps {
    const current = filters.statusId.map(String);
    const rowWord = rows.length === 1 ? '1 row' : `${rows.length} rows`;

    return {
      ...filter,
      /*
       * ⚠️ **The applied status always has a checkbox, even when the option list
       * does not know it.** The declared list already injects the URL's id, so
       * this only fires on the DEGRADED path — the master unread, the column
       * falling back to values accumulated from loaded rows, and the response
       * carrying none of them (a status filter that matched nothing, say).
       * Without it the funnel shows the column as filtered with nothing to
       * untick and the only way out is the URL.
       */
      options: withSelectedValues(filter.options, current),
      value: {
        selected: current,
        emptiness: filter.value.emptiness,
      },
      onChange: (next) => {
        // Emptiness only — the hook must never hold a status selection.
        if (next.emptiness !== filter.value.emptiness) {
          filter.onChange({ selected: [], emptiness: next.emptiness });
        }
        /*
         * Every ticked id, re-validated and de-duplicated by `parseIdList`, as
         * one CSV. `[]` joins to `''`, which deletes the key: `?statusId=` is a
         * 422, not "no filter". Skipped when nothing changed, so an emptiness
         * tick does not reset the pager.
         */
        const chosen = parseIdList(next.selected.join(',')).join(',');
        if (chosen !== current.join(',')) setFilter('statusId', chosen);
      },
      /*
       * ⚠️ **NOT HELPER TEXT — it is the panel's own scope footnote, which is
       * mandatory and renders whatever we do.** The default `server` sentence
       * would be true but silent about the thing a reader cannot see here: that
       * this control and the multiselect above are the same filter. A state, not
       * advice.
       */
      scopeNote:
        `The same filter as “All statuses” above, so either control moves both. ` +
        `Values re-query the whole table. ` +
        `Is empty / Is not empty apply only to the ${rowWord} loaded here.`,
      /*
       * The master is still loading, so the popover says so rather than "No
       * values on this page", which would read as a status list that is genuinely
       * empty. A FAILED load is deliberately NOT turned into `disabled`: that
       * would take the working sort control with it, and the Combobox beside this
       * already reports the failure in words.
       */
      loading: statuses.loading,
    };
  }

  function headerFor(key: string): FilterableTHProps {
    const props = table.headerFor(key);
    const filter = props.filter;
    if (!filter) return props;
    /*
     * ⚠️ Status brings its own page reset with it (`setFilter` calls `setPage(1)`
     * for the selection, and the emptiness half is page-scoped and must NOT
     * reset), so it deliberately skips the generic wrapper below rather than
     * being run through both.
     */
    const scoped: ColumnFilterProps =
      key === STATUS_COLUMN
        ? bridgeStatusToUrl(filter)
        : {
            ...filter,
            onChange: (next) => {
              filter.onChange(next);
              if (filter.scope === 'server') setPage(1);
            },
          };
    const sortControl = filter.sort;
    if (!sortControl) return { ...props, filter: scoped };
    return {
      ...props,
      filter: {
        ...scoped,
        sort: {
          direction: sortControl.direction,
          onSort: (direction) => {
            sortControl.onSort(direction);
            setPage(1);
          },
        },
      },
    };
  }

  const total = result?.meta.total ?? 0;
  // `meta.totalPages` is 0 (not 1) for an empty set, which would disable both
  // pager buttons and render "Page 1 of 0".
  const totalPages = Math.max(result?.meta.totalPages ?? 1, 1);
  // Show all is one list from the first row, whatever `page` still holds.
  const firstRow = total === 0 ? 0 : showAll ? 1 : (page - 1) * PER_PAGE + 1;
  const lastRow = total === 0 ? 0 : firstRow + rows.length - 1;
  const initialLoad = loading && !result;
  // Only the count that belongs to the invoice actually being confirmed; a
  // response that arrived for a previous dialog is ignored rather than shown.
  const confirmLineCount =
    pendingDelete && pendingLineCount?.id === pendingDelete.id ? pendingLineCount.count : null;

  /*
   * The SERVER-side filters, which is what the count in the panel description
   * describes. Each bill-date bound counts on its own: they are independently
   * settable and each narrows the request by itself.
   */
  const activeFilterCount =
    ID_FILTERS.filter((key) => filters[key] !== null).length +
    /*
     * ⚠️ **ONE per KEY, never one per selected id.** Three projects ticked is
     * one filter narrowing one request, and counting the chips would make the
     * panel say "4 filters" to somebody who set two. The test is
     * `length > 0` — an empty selection sends nothing, so it is not a filter at
     * all.
     */
    ID_LIST_FILTERS.filter((key) => filters[key].length > 0).length +
    DATE_FILTERS.filter((key) => filters[key] !== '').length +
    (filters.currency ? 1 : 0) +
    /*
     * ⚠️ **The Projects and From Xero funnels count HERE and not with the column
     * funnels below, because they are server filters that happen to be set from
     * a header.** They are added by counting the params actually SENT rather
     * than by asking whether a column has a selection: several values ticked
     * send nothing and fall back to narrowing the loaded page, and claiming a
     * server filter on that request would overstate what was asked.
     *
     * ⚠️ **`Object.keys` and not a truthiness test**, which matters now that one
     * of them is a boolean: `importedFromXero: false` is a real filter — the
     * "No" half of a genuine partition — and counting it by truthiness would drop
     * it from the sentence while it was narrowing the list.
     *
     * ⚠️ **STATUS IS NOT COUNTED HERE**, and that is not an omission: the funnel
     * writes `?statusId=`, so `ID_FILTERS` above has already counted it. Adding
     * it again would double-count the one filter set from two controls.
     */
    Object.keys(listFilters).length;
  /*
   * `table.activeCount` is added here and NOT to the count above, on purpose: a
   * PAGE-scoped column funnel narrows the loaded page rather than the request,
   * so folding it into a sentence about how many invoices matched would
   * overstate what the server was asked. It belongs here because this is what
   * decides whether the empty state offers "Clear filters" — and a reader who
   * has emptied the table with a funnel needs that button as much as one who
   * emptied it with a bound.
   *
   * ⚠️ *This used to read "a column funnel narrows the loaded page rather than
   * the request", flatly.* That was true while Bill date was the only funnel
   * here and stopped being true when the server-scoped Projects funnel arrived
   * (2026-09-02) — a sentence falsified by a sibling constant rather than by
   * anything in its own file. The Projects half is counted one line up; this
   * paragraph now describes only the page-scoped remainder.
   */
  const anyFilter = search !== '' || activeFilterCount > 0 || table.activeCount > 0;

  /*
   * The project behind `?projectId=N`, by NAME, and for free: every invoice
   * serialises `project: {id, name}`, so any row that matched already carries
   * it — the same trick `namedFromRows` plays for the company, and for the same
   * reason: a label is not worth a request against a 12,000-per-60s (⚠️ *not
   * the 120 this once said*) limiter. No
   * name at all when the filter came back empty, which is the normal case while
   * nothing has been billed against a project, and the id still identifies it.
   */
  const projectName =
    filters.projectId === null
      ? null
      : (rows.find((row) => row.project?.id === filters.projectId)?.project?.name ?? null);

  /*
   * What the "All projects" MULTISELECT offers: the active projects, plus every
   * id `?lineProjectId=` already names that is not among them.
   *
   * ⚠️ *SUPERSEDED 2026-09-02: "the active projects, plus whatever
   * `?lineProjectId=` already names when it is not among them" — singular.* The
   * control takes several ids now, so the injection is per id and **every**
   * missing one has to be injected: a chip whose option is absent renders as a
   * bare value or as nothing at all, and a selection the reader cannot see is
   * worse than one they cannot name.
   *
   * ⚠️ **`projectOptions()` is called once per MISSING id rather than
   * re-implemented for several**, so the label rule — the id fallback, and the
   * suffix it withholds while the list is empty — stays in the one shared
   * module. The base call carries the real list; each extra call is handed one
   * stored ref and only its injected head is kept. That maps `projects` once
   * more per missing id, which is nothing against a list capped at 100 and a
   * selection of a handful, and it is the price of not forking the helper.
   *
   * 🔴 **THE NAME IS DELIBERATELY LEFT BLANK, AND `projectName` ABOVE MUST NOT
   * BE REUSED HERE.** It is derived from `row.project`, which is the invoice's
   * HEADER project — a different question from the one this key asks, and null
   * on all 818 rows in any case. An invoice matching `?lineProjectId=7` can
   * carry a header project of 9, or of nothing; reading one as the other would
   * label this control with the name of a project the reader did not ask for.
   * That is the proxy-field shape exactly: a field written to answer one
   * question, consulted for another, agreeing often enough to look right.
   *
   * ⚠️ **A list row carries no line items, so there is NO cheap name available
   * for this key at all** — unlike the header filter, which gets one free off
   * any matched row. So an off-list project renders as `#7` via
   * `projectOptions`' own fallback. Honest and identifying; a request per label
   * against a 12,000/60s (⚠️ *not the 120/60s this once said*) limiter is not
   * worth a nicer word, and a name borrowed from the wrong column is not a name.
   *
   * ⚠️ **`projects.projects` is `[]` while the fetch is in flight AND when it
   * failed**, and `projectOptions` withholds the "(no longer offered)" suffix in
   * both — an empty list is not evidence a project was retired. A failed read
   * therefore shows the selection unlabelled rather than accusing it, and the
   * `Field` error beside the control says the list itself did not arrive.
   *
   * 🔴 **KNOWN, PRE-EXISTING, AND NOT INTRODUCED HERE: `projectOptions()` labels
   * an injected value "(no longer offered)" whenever it is absent from the list,
   * INCLUDING when the project is merely on PAGE 2 under `hasMore`.** A live,
   * active project can therefore be described as withdrawn — a confident wrong
   * claim of exactly the class this codebase keeps paying for, and the footnote
   * on the control ("Showing the first page of active projects") is the only
   * thing on screen that hints otherwise.
   *
   * ⚠️ **It cannot fire today and that is precisely why it will surprise
   * somebody**: `/projects` caps at 100 and production held ~31 projects
   * (CLAUDE.md, 2026-08-29 — a dated reading, not a constant), so `hasMore` is
   * false and every project is on page 1. The label is right for the only reason
   * it can currently be reached, which is retirement.
   *
   * ⚠️ **NOT fixed here on purpose.** `projectOptions()` is shared with the four
   * consumer forms, where the same suffix carries the same flaw; correcting it
   * for this one call site would either fork the helper or change what four
   * forms say. It goes back as one decision about the shared module.
   *
   * 🔴 **AND THE MULTISELECT WIDENS THE EXPOSURE, WHICH IS RECORDED RATHER THAN
   * PAPERED OVER.** One selection could be mislabelled before; several can be
   * now, and the wrong suffix appears once per missing chip. That is
   * unavoidable here — a selected id with no option renders as a bare value or
   * vanishes, and a selection the reader cannot see is worse than a mislabelled
   * one — so the injection stays and the flaw travels with it. Nothing about
   * WHEN it fires changed: it still needs `hasMore`, which is still false at ~31
   * projects against a cap of 100.
   */
  const lineProjectPickerOptions = useMemo(() => {
    const offered = projectOptions(projects.projects, null);
    const known = new Set(offered.map((option) => option.value));
    /*
     * One `projectOptions` call per id the list does not already carry, keeping
     * only the entry that call injected — identified by not being in `known`
     * rather than by position, so this does not depend on the helper continuing
     * to PREPEND. Nothing is injected for an id already offered, which is the
     * ordinary case and the one that must cost nothing.
     */
    const missing = filters.lineProjectId
      .filter((id) => !known.has(String(id)))
      .flatMap((id) =>
        projectOptions(projects.projects, { id, name: '' }).filter(
          (option) => !known.has(option.value),
        ),
      );
    return [...missing, ...offered];
  }, [projects.projects, filters.lineProjectId]);

  /*
   * The account lead behind `?accountLeadId=N`, by name, on the same terms as
   * `projectName` above: every invoice serialises `accountLead`, so a matched
   * row already carries the label and it costs no request. Null when nothing
   * matched — which is the ordinary case, since the filter is usually followed
   * precisely because the person has no invoices.
   *
   * Rendered defensively: a `UserSummary` can carry `name: ''`, and an empty
   * string in a badge reads as a missing record rather than an unnamed one.
   */
  const accountLeadName =
    filters.accountLeadId === null
      ? null
      : (rows
          .find((row) => row.accountLead?.id === filters.accountLeadId)
          ?.accountLead?.name.trim() || null);

  /*
   * Written out key by key, and only with keys the INVOICES board accepts: the
   * schema is strict per board, so a single stray key — or one belonging to
   * the deals or quotes board — 422s the whole request. `filters` is never
   * spread in here for that reason.
   *
   * `filters` is memoised on the query string, so this object is stable between
   * renders — and `useKanban` keys its effect on a serialisation of it anyway,
   * so a stray new reference would not loop, it would just be wasted work.
   */
  const boardParams = useMemo<KanbanQueryParams>(() => {
    const params: KanbanQueryParams = { cardsPerColumn: BOARD_CARDS_PER_COLUMN };
    if (search) params.search = search;
    if (filters.companyId !== null) params.companyId = filters.companyId;
    if (filters.contactId !== null) params.contactId = filters.contactId;
    /*
     * DECLARED by this board, so it is sent — the per-board table in the
     * backend's `docs/api/kanban.md` lists `accountLeadId` on all four boards,
     * and since 2026-08-28 the invoices board resolves it exactly as
     * `GET /invoices` does (the invoice's own override, else its company's). So
     * the two views narrow to the same set and a board count may be read beside
     * a list count.
     *
     * A SINGLE id, never the comma-separated form: that is companies-only and a
     * 422 here. `parseId` already guarantees one integer, so the CSV shape is
     * unreachable from this page.
     */
    if (filters.accountLeadId !== null) params.accountLeadId = filters.accountLeadId;
    // `statusId`, never `stageId`: invoices group by status, and the deals key
    // is rejected outright here rather than ignored. Narrowing a status-grouped
    // board to one status leaves one populated column — a legitimate thing to
    // ask for, and the header still counts every match. Several ids go as one
    // CSV, which the invoices board ORs since 2026-10-28.
    if (filters.statusId.length > 0) params.statusId = filters.statusId.join(',');
    // Both of these arrive by link from a deal or a quote rather than from a
    // control on this page, which is exactly how they used to go missing on the
    // board while the "Also filtered by" badges still named them.
    if (filters.dealId !== null) params.dealId = filters.dealId;
    if (filters.quoteId !== null) params.quoteId = filters.quoteId;
    // Already `^[A-Z]{3}$` by the time it reaches here; the board upper-cases
    // server-side as well, so both views agree on `usd` and `USD` alike.
    if (filters.currency !== null) params.currency = filters.currency;
    /*
     * `filters.projectId` is DELIBERATELY NOT SENT. `GET /invoices` takes it;
     * the invoices board does not declare it, and the schema is `.strict()`, so
     * this one key would 422 the whole board rather than being ignored. A board
     * that ignores a filter is bad; a board that 422s is worse. The notice
     * below the badge tells the reader instead of letting the two views
     * disagree in silence.
     *
     * 🔴 **`filters.lineProjectId` is NOT SENT either, and it is the one a
     * reader is most likely to add here** — it is the project filter with a
     * control, so it feels like the real one. No board declares a project key of
     * any kind (checked against the backend's `kanban.registry.ts`, all five
     * boards), so it 422s exactly as its header twin would. Both are named in
     * `boardCannotFilterBy` instead.
     *
     * ⚠️ **It is refused TWICE OVER now that it holds several ids, and neither
     * half is a way in.** The board declares no project key, and no board parses
     * a comma-separated value for ANY key it does declare — so there is no
     * "send just the first one" that would be honest either. A board narrowed by
     * one of the reader's three projects, under a heading naming all three,
     * would be worse than the notice.
     *
     * 🔴 **`billDateAfter` / `billDateBefore` are NOT SENT either, and for the
     * same reason.** The invoices board declares the two `dueDate` bounds and
     * NOT the two `billDate` ones, so sending either would 422 the whole board
     * — and the near-miss is the hazard: the board DOES take a date window, just
     * on the other column, so `dueDateAfter` is sitting right there looking like
     * the key to reuse. It answers a different question and quietly filtering by
     * it would be worse than not filtering at all. Same notice, same reasoning.
     */
    return params;
  }, [search, filters]);

  /*
   * ⚠️ **ONE writer for every URL-backed filter on this page, the multi-id one
   * included.** It takes the value already spelled the way the query string
   * holds it — a CSV for a list — so there is no second place that decides how
   * a selection is encoded, and `''` keeps meaning "delete the key" for all of
   * them. That is what keeps "an empty selection sends nothing" a property of
   * the mechanism rather than of whichever control remembered it.
   */
  const setFilter = useCallback(
    (key: IdFilter | IdListFilterKey | DateFilter | 'currency', value: string) => {
      setPage(1);
      const next = new URLSearchParams(queryString);
      if (value === '') next.delete(key);
      else next.set(key, value);
      setSearchParams(next, { replace: true });
    },
    [queryString, setSearchParams],
  );

  function clearFilters() {
    setSearch('');
    setPage(1);
    // The column funnels live in the hook, not in the URL, so wiping the query
    // string alone would leave the table narrowed by a filter the reader has
    // just been told is cleared. `clearAll` deliberately leaves the SORT alone —
    // reordering the table from a button labelled "clear filters" is a surprise.
    table.clearAll();
    // Clearing the filters must not also throw the user into the other view:
    // `view` is not a filter, so it survives an empty search-param write. The
    // TABLE is the default, so it is the BOARD that has to be re-spelled here.
    const next = new URLSearchParams();
    if (view === 'board') next.set('view', 'board');
    setSearchParams(next, { replace: true });
  }

  /*
   * Deleting an invoice cascades its line items away silently — no 409, no
   * count in the response, nothing to undo. The count is fetched first so the
   * dialog can name it, because the API never will and this is the only moment
   * the user could have been told.
   */
  async function openDelete(invoice: Invoice) {
    setDeleteError(null);
    setPendingLineCount(null);
    setPendingDelete(invoice);
    try {
      const lines = await api.listInvoiceProducts(invoice.id);
      setPendingLineCount({ id: invoice.id, count: lines.data.length });
    } catch {
      // A failed count must not block the delete — the dialog falls back to the
      // generic warning rather than claiming there are none. Only this
      // invoice's own stamp is cleared, so a later dialog keeps its good count.
      setPendingLineCount((prev) => (prev?.id === invoice.id ? null : prev));
    }
  }

  async function confirmDelete() {
    if (!pendingDelete) return;
    setDeleting(true);
    setDeleteError(null);
    try {
      await api.deleteInvoice(pendingDelete.id);
      setPendingDelete(null);
      // Removing the last row of a page would strand the user on an empty final
      // page; stepping back refetches through the page effect.
      if (rows.length === 1 && page > 1) setPage((p) => p - 1);
      else await load();
    } catch (caught) {
      if (caught instanceof ApiError && caught.status === 404) {
        // Already gone is the outcome that was wanted — treat it as success.
        setPendingDelete(null);
        if (rows.length === 1 && page > 1) setPage((p) => p - 1);
        else await load();
      } else if (caught instanceof ApiError && caught.status === 403) {
        /*
         * A 403 body carries NO `details`, so there is nothing to render per
         * field — it is a banner or it is nothing. `denialReason` is asked
         * first in case the ability already knows why; when the ability
         * believed the delete was allowed it returns null, and the fallback
         * covers the case the client cannot explain.
         */
        setDeleteError(
          denialReason(ability, 'delete', 'invoices') ??
            'Your account can no longer delete invoices. Ask an administrator.',
        );
      } else {
        setDeleteError(caught instanceof Error ? caught.message : 'Delete failed');
      }
    } finally {
      setDeleting(false);
    }
  }

  /*
   * ⚠️ **`handleXeroSettled` was here and went with the Xero column on
   * 2026-08-31.** It was the ONLY producer of `xeroReport` on this page, and the
   * only writer of `listRefresh` — it recorded the push outcome, re-read the row
   * (a refused push still moves `xero.status`, so the report alone would have
   * left the button claiming something from before the attempt), and bumped the
   * board, which holds its own copy of these records.
   *
   * Removed together with the column rather than left behind: a settle handler
   * with nothing that can settle is a live-looking path to a control that is
   * gone. `setBoardRefresh` survives — `handleSaved` below still bumps it.
   */
  /*
   * The line grid inside the form saved on its OWN, and the modal is still
   * open.
   *
   * ⚠️ **THIS IS NOT A SMALLER `handleSaved`, AND IT MUST NOT CLOSE THE FORM.**
   * The person has not finished — they pressed "Save N rows" at the top of the
   * line-items band and are still editing. Everything `handleSaved` does about
   * *finishing* (`setFormState(null)`, and the create-lands-on-page-1 jump)
   * would take the form away from underneath them.
   *
   * ══ WHY IT EXISTS AT ALL ══════════════════════════════════════════════════
   *
   * The grid's own Save is a second way to write line items, and it reported
   * NOTHING upward. So: open an invoice, fix the project on two lines, press
   * "Save 2 rows", watch each row report success — and then the only way out is
   * a button reading **Cancel**, because the footer Save has correctly greyed
   * out with nothing left to send. Cancel routes to `closeForm`, which does not
   * reload. The row's **Projects** badge then still read its old value while
   * the database said otherwise, and a re-sort by that column disagreed with
   * the badge sitting next to it. Nothing errored, and a manual refresh "fixed"
   * it — which is what made it read as the column being flaky rather than as a
   * missing refetch.
   *
   * ⚠️ `lineProjectStatus` is computed live per request from the line table, so
   * it can never be stale ON THE SERVER. Every stale badge on this column is
   * this page holding an old response, which is why the fix belongs here and
   * not in the derivation.
   *
   * ⚠️ It is not only that column. A line write REWRITES ALL FOUR HEADER TOTALS
   * inside its own transaction, so the grand total in this table is stale too —
   * and a wrong figure in a money column is the half of this nobody would file
   * as a refresh bug.
   *
   * ⚠️ Refetching UNDERNEATH AN OPEN MODAL is deliberate, not premature. The
   * row behind the dialog is wrong the instant those lines land; waiting for
   * the close would mean picking one of four exit paths (Cancel, the X,
   * Escape, the overlay) and getting the other three wrong. `formState` holds
   * its own copy of the invoice, so a reload of `rows` cannot disturb the form.
   *
   * The board is bumped for the same reason `handleSaved` bumps it — it holds
   * its own copy of these records and has no idea a save happened.
   */
  function handleLinesSaved() {
    setBoardRefresh((token) => token + 1);
    void load();
  }

  function handleSaved() {
    const wasCreate = !formState?.invoice;
    setFormState(null);
    /*
     * The board holds its own copy of the records and has no idea a save
     * happened, so it is told to refetch here. Bumped on an edit too, not just
     * a create: the card carries the subject, the Xero invoice number, the
     * total and the due date, and a status change made in the form moves the
     * card to another column outright.
     */
    setBoardRefresh((token) => token + 1);
    // A new invoice gets the highest id, so under `id DESC` it lands on page 1.
    if (wasCreate && page !== 1) setPage(1);
    else void load();
  }

  const countLabel = initialLoad
    ? 'Loading…'
    : `${total} ${total === 1 ? 'invoice' : 'invoices'}${search ? ` matching “${search}”` : ''}${
        activeFilterCount > 0
          ? ` (${activeFilterCount} ${activeFilterCount === 1 ? 'filter' : 'filters'})`
          : ''
      }`;

  /*
   * The board must not describe itself with the table's totals — they belong to
   * a request the board view never made, and they would freeze at whatever the
   * last table load happened to say. The columns carry their own counts, and
   * those count every match of the filters in force, not the 20 cards rendered.
   */
  /*
   * The filters this page can set that the BOARD cannot take — named, because
   * the board silently showing an unnarrowed set under a filtered heading is the
   * "it looks like it worked" failure this exists to prevent. Both are absent
   * from `boardParams`; sending either is a 422 on a `.strict()` per-board
   * schema, not a no-op.
   */
  const boardCannotFilterBy: string[] = [];
  if (filters.projectId !== null) boardCannotFilterBy.push('project');
  /*
   * ⚠️ **`lineProjectId` joined this list on 2026-09-02, and it is NOT covered
   * by the `projectId` line above it.** They are separate keys with separate
   * clauses: setting only the line filter leaves `filters.projectId` null, so
   * without this the board would render unnarrowed under a filtered heading —
   * the exact failure this list exists to prevent.
   *
   * ⚠️ **NO board declares a project key of ANY kind** — confirmed against the
   * backend's `kanban.registry.ts`, which contains neither `projectId` nor
   * `lineProjectId` for any of the five boards. So this is not a near miss like
   * From Xero; the schemas are `.strict()` and either key would 422 the whole
   * board rather than being ignored.
   *
   * **"billed project" and not "project"**, because `projectId` above already
   * claims that word and `lineProjectStatus` below claims "projects". Three
   * project-ish filters can be set at once and the sentence lists them by name,
   * so a reader has to be able to tell which of the three was dropped.
   *
   * 🔴 **`length > 0`, so ANY selection fires it — and a CSV would not merely
   * be dropped, it would 422 twice over.** No board declares the key in any
   * form, and no board parses a comma-separated value for any key it does have.
   *
   * **Singular even with several ticked**, deliberately: the sentence is a list
   * of filter NAMES joined by `formatList`, and pluralising one member would
   * read as a second entry in that list ("the billed projects and bill date
   * filters do not apply") rather than as one filter holding several values.
   * `boardCannotFilterBy.length` — how many FILTERS were dropped — is what
   * chooses "filter" or "filters" below, and it must keep counting keys.
   */
  if (filters.lineProjectId.length > 0) boardCannotFilterBy.push('billed project');
  if (filters.billDateAfter !== '' || filters.billDateBefore !== '') {
    boardCannotFilterBy.push('bill date');
  }
  /*
   * ⚠️ **The Projects funnel joined this list on 2026-09-02**, and it is the
   * first entry here that does NOT come from the URL — it lives in
   * `useTableFilters`' own state. It belongs here all the same: what the
   * sentence is about is whether the BOARD was narrowed by something the TABLE
   * was, and where the value is stored has nothing to do with that.
   *
   * No board declares any line-item key, so this one is not even a near miss.
   */
  if (listFilters.lineProjectStatus !== undefined) boardCannotFilterBy.push('projects');
  /*
   * ⚠️ **From Xero joined this list on 2026-09-02, and it is a NEAR MISS rather
   * than an obvious absence.** `GET /invoices` takes `importedFromXero`; the
   * invoices BOARD does not declare it, and that schema is `.strict()` per board
   * — so sending it would 422 the whole board rather than being ignored, exactly
   * like `projectId` and the two `billDate` bounds above.
   *
   * ⚠️ **`!== undefined`, never a truthiness test.** `importedFromXero: false`
   * is a real filter here — the "No" half of a partition, not "no filter" — so
   * `if (listFilters.importedFromXero)` would silently drop the notice for every
   * reader who had filtered to the invoices EMERGE raised, which is precisely
   * the set the board would then show unnarrowed.
   */
  if (listFilters.importedFromXero !== undefined) boardCannotFilterBy.push('from Xero');
  // Same near miss: the invoices board does not declare `sentToClient`.
  if (listFilters.sentToClient !== undefined) boardCannotFilterBy.push('sent to client');
  /*
   * ⚠️ **THE `length === 2` TERNARY IS GONE, and that is not a tidy-up.** It
   * produced `undefined` for THREE names — falling off its own else branch into
   * `boardCannotFilterBy[0]`, so a reader with three filters set would have been
   * told about one of them and never known the other two were unapplied. It was
   * correct for exactly the two cases that existed when it was written, which is
   * the shape that breaks silently the moment a third arrives.
   */
  const boardUnappliedLabel =
    boardCannotFilterBy.length === 0 ? undefined : formatList(boardCannotFilterBy);

  const panelDescription =
    view === 'board'
      ? // "Status", never "stage": this board's column key is `statusId`.
        //
        // The blanket "the filters above apply here too" stops being true the
        // moment a filter the board cannot take is set. A description that
        // overstates what a view is showing is how a filtered-looking board gets
        // read as the whole picture.
        boardUnappliedLabel !== undefined
        ? `Grouped by status. The ${boardUnappliedLabel} ${
            boardCannotFilterBy.length > 1 ? 'filters do' : 'filter does'
          } not apply here.`
        : 'Grouped by status. Each column shows the newest 20 matches.'
      : countLabel;

  const newInvoiceButton = canCreate ? (
    <Button variant="primary" leadingIcon={<PlusIcon />} onClick={() => setFormState({})}>
      New invoice
    </Button>
  ) : null;

  const emptyState = (
    <InvoicesEmptyState
      filtered={anyFilter}
      projectFiltered={filters.projectId !== null}
      onClear={clearFilters}
      hasError={Boolean(error)}
      action={newInvoiceButton}
      denial={createDenial}
    />
  );

  /*
   * ⚠️ Below every hook, so the early return cannot change the hook order. The
   * refusal is rendered here rather than by the route, which carries no guard.
   */
  if (!canReadModule) {
    return (
      <>
        <PageHeader eyebrow="Operations" title="Invoices" />
        <Panel className="min-w-0">
          <EmptyState
            title="This page isn’t available to your account"
            description="Your permission set does not include this. An administrator can change that."
          />
        </Panel>
      </>
    );
  }

  return (
    <>
      <PageHeader
        eyebrow="Operations"
        title="Invoices"
        actions={
          <>
            <ViewToggle view={view} onChange={setView} />
            {newInvoiceButton}
          </>
        }
      />

      <Panel>
        <PanelHeader title="All invoices" description={panelDescription} />

        {/*
          * The filters get their own full-width row rather than PanelHeader's
          * `actions` slot: that slot is `shrink-0`, so a bank of controls in it
          * can neither shrink nor wrap and blows the page out sideways below
          * 1280px. This was a real bug on DealsPage.
          */}
        <div className="flex flex-wrap items-center gap-2 border-b border-border px-4 py-3">
          <div className="w-full min-w-0 sm:w-56">
            <Field label="Search invoices" hideLabel>
              {({ id }) => (
                <Input
                  id={id}
                  type="search"
                  // The API matches `subject` and nothing else — not the Xero
                  // number, not the company. Promising more is a lie the
                  // results immediately expose.
                  placeholder="Search subject…"
                  leadingIcon={<SearchIcon />}
                  value={search}
                  onChange={(e) => {
                    setSearch(e.target.value);
                    setPage(1);
                  }}
                />
              )}
            </Field>
          </div>
          <div className="w-full min-w-0 sm:w-48">
            <Field label="Filter by status" hideLabel>
              {({ id }) => (
                <MultiCombobox
                  id={id}
                  values={filters.statusId.map(String)}
                  // `[]` joins to `''`, which deletes the key.
                  onChange={(values) => setFilter('statusId', values.join(','))}
                  options={statusOptions}
                  placeholder="All statuses"
                  searchPlaceholder="Search statuses…"
                  maxVisibleChips={0}
                  summaryNoun={{ singular: 'status', plural: 'statuses' }}
                  loading={statuses.loading}
                  {...(statuses.failed ? { invalid: true } : {})}
                />
              )}
            </Field>
          </div>
          <div className="w-full min-w-0 sm:w-40">
            {/* The invalid border was the only signal here; it needs words
                beside it, saying the empty list is a failure not an answer. */}
            <Field
              label="Filter by currency"
              hideLabel
              {...(currencies.failed
                ? { error: 'Couldn’t load currencies.' }
                : {})}
            >
              {({ id, 'aria-describedby': describedBy }) => (
                <Combobox
                  id={id}
                  value={filters.currency ?? ''}
                  onChange={(value) => setFilter('currency', value)}
                  options={currencyPickerOptions}
                  emptyOptionLabel="All currencies"
                  placeholder="All currencies"
                  searchPlaceholder="Search currencies…"
                  loading={currencies.loading}
                  aria-describedby={describedBy}
                  {...(currencies.failed ? { invalid: true } : {})}
                />
              )}
            </Field>
          </div>
          {/*
            ⚠️ THE "ALL OWNERS" FILTER STOOD HERE; THE SLOT IS DELIBERATELY
            EMPTY. Owner is gone from invoices (owner's instruction,
            2026-08-31) and `ownerId` is not a member of `ID_FILTERS`, so a
            stale `?ownerId=` in a URL is read as absent rather than honoured.
          */}
          {/*
            * "Which invoices bill any of these projects" — `?lineProjectId=`,
            * matched on the LINE ITEMS.
            *
            * 🔴 **A MULTISELECT SINCE 2026-09-02, AND IT WAS NEVER BROKEN — IT
            * WAS NEVER MULTI.** *SUPERSEDED: "Which invoices bill this project"
            * over a single-select `Combobox` writing one id.* The owner reported
            * it as not working, which is what a control that offers no way to
            * pick a second value looks like from the outside. Several ids go out
            * as one comma-separated value and mean **OR**.
            *
            * 🔴 **THIS IS NOT THE `?projectId=` BADGE BELOW.** That one matches
            * the dead header column and is populated on 0 of 818 invoices; this
            * one matches the 302 lines that actually name a project. Two keys a
            * prefix apart, two questions — the table in `ID_LIST_FILTERS` is the
            * long version and is worth reading before changing either.
            *
            * ⚠️ **It writes through the SAME `setFilter` as the selects beside
            * it**, so one key has one writer: this control and "Clear" move the
            * same query parameter, and it is URL-backed, saved and counted on
            * exactly the terms every other id filter here is. The only thing
            * this control decides is the ENCODING — a CSV of the chips, or `''`
            * for none, which is what deletes the key rather than writing it
            * empty.
            *
            * ⚠️ **The board cannot narrow by it and this control does not say
            * so.** The notice under the filter bank does, for every refused key
            * at once. A second sentence attached to the control would be helper
            * text, and would drift from the one that has to stay correct.
            *
            * `sm:w-48` matches the status select rather than the narrower
            * currency one: project names run long ("BSF India - Summer 2027").
            * The trigger counts rather than listing chips at that width — see
            * `maxVisibleChips` below.
            */}
          <div className="w-full min-w-0 sm:w-48">
            {/* Same treatment as currency: the invalid border alone says
                something is wrong without saying that the EMPTY LIST is the
                failure rather than the answer. */}
            <Field
              label="Filter by the projects its lines bill"
              hideLabel
              {...(projects.failed ? { error: 'Couldn’t load projects.' } : {})}
            >
              {({ id, 'aria-describedby': describedBy }) => (
                <MultiCombobox
                  id={id}
                  values={filters.lineProjectId.map(String)}
                  /*
                   * ⛔ **An empty selection writes `''`, which DELETES the key.**
                   * `?lineProjectId=` is a 422 on this `.strict()` schema, not
                   * "no filter", so the empty array must never become an empty
                   * CSV — `join(',')` on `[]` is already `''`, and that is the
                   * value `setFilter` reads as "remove me from the URL".
                   *
                   * Order is the order the reader ticked in. The server ORs the
                   * members, so it carries no meaning — but re-sorting it here
                   * would rewrite the URL under somebody mid-selection.
                   */
                  onChange={(values) => setFilter('lineProjectId', values.join(','))}
                  options={lineProjectPickerOptions}
                  placeholder="All projects"
                  searchPlaceholder="Search projects…"
                  /*
                   * ⚠️ **Counted, not chipped, at `sm:w-48`.** Project names run
                   * long, so two chips wrap the trigger onto three lines and
                   * shove the bill-date pair off the row. The noun is what makes
                   * the count readable — and it is the trigger's accessible name
                   * as well, so "2 projects" is what a screen reader gets rather
                   * than "2 selected".
                   */
                  maxVisibleChips={0}
                  summaryNoun={{ singular: 'project', plural: 'projects' }}
                  loading={projects.loading}
                  aria-describedby={describedBy}
                  {...(projects.failed ? { invalid: true } : {})}
                  /*
                   * 🔴 **`'even-when-truncated'`, NOT A BARE `true`** — the
                   * same carve-out as the twin control on `/quotes`, which
                   * carries the long version. A bare `true` draws nothing here
                   * because the truncation footnote below fires at 113 active
                   * projects against a ceiling of 100.
                   *
                   * ⚠️ **The two pages reached this independently and must stay
                   * in step**, since one control's reader has no way to know
                   * the other exists. ⛔ And the value is for FILTERS only —
                   * on a stored set it would claim a completeness it has not
                   * got.
                   */
                  selectAll="even-when-truncated"
                  {...(projects.hasMore
                    ? {
                        /*
                         * `/projects` caps `perPage` at 100 and there is no
                         * "give me all". A picker silently missing its 101st
                         * option is indistinguishable from that project not
                         * existing, and the person who cannot find it has no
                         * way to tell which — so the truncation is stated. A
                         * fact about the data rather than advice, which is why
                         * it survives the no-helper-text rule, and it renders
                         * inside the popover where the missing options would
                         * have been.
                         */
                        footnote: 'Showing the first page of active projects.',
                      }
                    : {})}
                />
              )}
            </Field>
          </div>
          {/*
            * The bill-date window — what the business calls the invoice's issue
            * date. Wider than the selects beside it because it holds two boxes
            * and a legend; `basis-36` inside it is what lets the pair drop onto
            * two lines at 320px rather than shrinking the boxes to nothing.
            *
            * ⚠️ **`sm:w-80` IS ARITHMETIC, NOT A ROUNDED-UP GUESS, AND
            * RE-NARROWING IT STACKS THE PAIR AGAIN.** The two boxes ask for
            * `basis-36` twice plus a `gap-2` — 144 + 144 + 8 = **296px** — so at
            * the `sm:w-72` (288px) this carried until 2026-09-12 the row was
            * EIGHT PIXELS short and the flex wrapped, putting From above To at
            * every width from `sm` up. 20rem is 320px, which clears the sum and
            * leaves each box 156px. Reported by the owner off the live page.
            *
            * ⚠️ Nothing failed and nothing overflowed — a wrapped flex row is a
            * correct render of what it was asked for, so the only symptom was
            * two boxes stacked where one line was intended. **Move this width
            * and the `basis-36` inside together, or not at all.**
            *
            * Below `sm` the wrapper is `w-full` and the pair still wraps at
            * 320px, deliberately: 156px each is comfortable, ~140px starts
            * clipping `dd-mm-yyyy` against the native picker icon.
            */}
          {/*
            * The column manager, last in the bank so it sits after the controls
            * that change WHICH ROWS and before nothing — it is the only one here
            * that changes what the table SHOWS rather than what it asks for.
            *
            * ⚠️ **TABLE VIEW ONLY, AND ONLY FROM `md`.** The board has no columns
            * to arrange, and below `md` this page renders cards rather than a
            * table — a "Columns" button over a card list would silently rearrange
            * a table the reader cannot see, and the layout persists against the
            * account, so it would still be rearranged on their laptop tomorrow.
            * That is NOT the "a feature reachable only above a breakpoint is
            * indistinguishable from one that was removed" case the card list
            * below is careful about: there is no column set at these widths for
            * this control to be the mobile form of. The FILTERS, which do exist
            * at every width, keep theirs in `FilterSheet`.
            *
            * ⚠️ **A WRAPPER `<div>` rather than `className="hidden md:block"` on
            * the trigger.** `Button` bakes in `inline-flex`, and `hidden` is the
            * same CSS property, so which won would be decided by the emitted
            * stylesheet's order rather than by the order they are written in —
            * the trap `Table.tsx` records twice against `wrap` and `verticalAlign`.
            */}
          {view === 'table' && (
            <div className="hidden md:block">
              <ColumnManager
                layout={layout}
                labelOf={columnLabel}
                lockedKeys={LOCKED_COLUMN_KEYS}
                pinnedKeys={PINNED_COLUMN_KEYS}
                lockReasonOf={lockReasonOfColumn}
              />
            </div>
          )}
          <div className="w-full min-w-0 sm:w-80">
            <BillDateRangeFilter
              from={filters.billDateAfter}
              to={filters.billDateBefore}
              /*
               * Re-validated on the way IN as well as on the way out. A native
               * date box normally hands back '' or a real `YYYY-MM-DD`, but a
               * partially-typed value in a text-input fallback does not — and an
               * unreal date would reach the URL, then the request, then a 422
               * naming a parameter the reader never typed.
               */
              onFrom={(value) => setFilter('billDateAfter', dateBound(value))}
              onTo={(value) => setFilter('billDateBefore', dateBound(value))}
            />
          </div>
        </div>

        {/*
          * Contact, company, deal and quote are filterable on the API but have
          * no control here: they are arrived at by link from those records.
          * They still have to be visible, or the list looks short for no
          * reason.
          *
          * ⚠️ **The test for membership is *"the API takes it and this page
          * offers no way to SET it"*** — not "it is a filter". A key with a
          * control does not belong here: the badge would announce one filter
          * twice, and the badge is the copy that cannot be cleared from where it
          * is read.
          *
          * 🔴 **So `lineProjectId` has NO BADGE and must not be given one** — it
          * gained an "All projects" picker in the bank above on 2026-09-02, and
          * that picker became a MULTISELECT the same day. **Holding several ids
          * is not a reason to add one**: the rule is about whether the reader can
          * SEE and CLEAR the filter where it is set, and the trigger says "2
          * projects" at every width. A badge would name the same filter twice
          * and would be the copy that cannot be cleared from where it is read.
          *
          * **`projectId` KEEPS its badge**, because it still has no control and
          * is still only reachable by link. The two are one prefix apart and the
          * asymmetry is deliberate; it follows the rule above rather than
          * breaking it.
          */}
        {(filters.contactId !== null ||
          filters.companyId !== null ||
          filters.dealId !== null ||
          filters.quoteId !== null ||
          filters.accountLeadId !== null ||
          filters.projectId !== null) && (
          <div className="flex flex-wrap items-center gap-2 border-b border-border px-4 py-2">
            <span className="text-xs text-fg-muted">Also filtered by</span>
            {filters.companyId !== null && <Badge tone="neutral" variant="outline">Company #{filters.companyId}</Badge>}
            {filters.contactId !== null && <Badge tone="neutral" variant="outline">Contact #{filters.contactId}</Badge>}
            {filters.dealId !== null && <Badge tone="neutral" variant="outline">Deal #{filters.dealId}</Badge>}
            {/* Several invoices can share one quote — staged billing — so this
                is an ordinary multi-row view, not a one-hit lookup. */}
            {filters.quoteId !== null && <Badge tone="neutral" variant="outline">Quote #{filters.quoteId}</Badge>}
            {/* Says which relationship it scoped by, not just "mine": account
                lead and owner are different people, and a reader who assumes
                the wrong one reads an empty list as a fault. */}
            {filters.accountLeadId !== null && (
              <Badge tone="neutral" variant="outline">
                {accountLeadName === null
                  ? `Account lead #${filters.accountLeadId}`
                  : `Account lead: ${accountLeadName}`}
              </Badge>
            )}
            {/* Named where a row could name it, and by id where none could —
                the list is usually empty under this filter, and an id is still
                an identifier where a blank would be nothing at all. */}
            {filters.projectId !== null && (
              <Badge tone="neutral" variant="outline">
                {projectName === null ? `Project #${filters.projectId}` : `Project: ${projectName}`}
              </Badge>
            )}
            <Button variant="ghost" size="sm" onClick={clearFilters}>
              Clear
            </Button>
          </div>
        )}

        {/*
          * The filters on this page the board cannot take. Said next to the
          * controls that would otherwise imply they had been applied to both
          * views — a filtered table and an unfiltered board under one heading is
          * exactly the "it looks like it worked" failure this is fixing.
          *
          * ⚠️ It names WHICH filters rather than saying "some filters", because
          * the reader's next move differs: a project filter is one they arrived
          * on by link, a bill-date window is one they set in the bank above and
          * can clear where they set it.
          */}
        {view === 'board' && boardUnappliedLabel !== undefined && (
          <div className="border-b border-border px-4 py-2">
            <p className="m-0 text-xs text-fg-muted">
              The board is <strong className="font-semibold">not</strong> narrowed by the{' '}
              {boardUnappliedLabel}{' '}
              {boardCannotFilterBy.length > 1 ? 'filters' : 'filter'}. Switch to Table to apply{' '}
              {boardCannotFilterBy.length > 1 ? 'them' : 'it'}.
            </p>
          </div>
        )}

        <p role="status" aria-live="polite" className="sr-only">
          {panelDescription}
        </p>

        {view === 'board' ? (
          /*
           * `min-w-0` so the board's own horizontal scroller is what scrolls —
           * a flex/grid child defaults to `min-width: auto` and would widen the
           * page instead, which breaks 320px.
           */
          <div className="min-w-0 px-4 py-3">
            <KanbanBoard
              board="invoices"
              params={boardParams}
              hrefFor={(id) => `/invoices/${id}`}
              /*
               * `PATCH /invoices/:id/status` takes WRITE_ROLES, so moving a
               * card is an update — not the admin act the old gating implied.
               */
              canMove={canUpdate}
              // False keeps the board from fetching at all while the table is
              // the visible view; it is only mounted here, but the prop is the
              // contract and stating it keeps that true if this moves.
              enabled={view === 'board'}
              /*
               * The SAME modal the header button and the table rows open —
               * `setFormState({})` is the one way this page opens a create
               * form, so there is no second piece of state to drift. Omitted
               * outright without `canCreate`, which is what hides the control
               * from a viewer rather than showing them a 403 in waiting.
               */
              create={
                canCreate
                  ? {
                      label: 'New invoice',
                      onCreate: () => setFormState({}),
                      refreshToken: boardRefresh,
                    }
                  : undefined
              }
              /*
               * No `cardAction`, and the REASON changed on 2026-09-15 even
               * though the answer did not.
               *
               * ⚠️ *SUPERSEDED: "Companies and deals pass one because their
               * table rows carry a 'raise a document from this record'
               * control; an invoice row carries no such action — **nothing is
               * raised FROM an invoice, it is the end of the chain.**"* **A
               * credit note is raised from an invoice**, so an invoice row now
               * carries exactly that control and the old argument is dead.
               * This is the "an artefact accurate about what it DESCRIBES is
               * falsified by a change somewhere else" species — the sentence
               * never moved, the world it described did.
               *
               * 🔴 **WHAT KEEPS IT OFF THE BOARD IS THE CARD DATUM, and it is
               * measured: `KanbanCard` CARRIES NO STATUS.** It holds id,
               * title, reference, amount, currency, date, company, contact and
               * owner — and no `statusId` on any of the five boards. The
               * credit-note gate reads a FLAG OFF THE STATUS, so a card cannot
               * answer it — and that stays true whichever flag the rule moves
               * to, because the missing datum is the status itself. The only
               * two things `cardAction` could render here are a control offered
               * BLIND on every card, drafts included — money handed back
               * against a document the customer has never been billed for — or
               * one disabled on every card for a reason that is about this
               * board rather than about the record.
               *
               * ⚠️ **This is NOT the `/companies` "the owner reported the
               * feature as DELETED, not hidden" case**, and the difference is
               * that the control has a second home one click away: the whole
               * card is a stretched link to `/invoices/{id}`, and
               * `InvoiceDetailPage` carries this control in its header. That
               * is the same test the paragraph above applies to Edit and
               * Delete. **Adding it here needs `statusId` on the card, not a
               * `cardAction`.**
               *
               * The rest stands unchanged: tasks-and-notes open on the
               * record's own page, and Delete is not an action to put one
               * mis-drag away on a board.
               */
            />
            {!canUpdate && moveDenial && (
              <p className="mt-3 m-0 text-xs text-fg-subtle">Cards can’t be moved. {moveDenial}</p>
            )}
          </div>
        ) : (
          <>
          {error && (
            <Notice
              tone="danger"
              role="alert"
              title={<>Couldn&rsquo;t load invoices.</>}
              className="mx-4 my-3"
            >
              <span className="min-w-0 [overflow-wrap:anywhere]">{error}</span>
            </Notice>
          )}

          {/*
            * ⚠️ **`XeroPushReportBanner` rendered here until 2026-08-31** — the
            * one place a push's outcome, including the server's own refusal text,
            * was shown. It went with the column that produced it: with no push
            * raised from this page there is no outcome to report.
            *
            * ⚠️ **The banner component is NOT gone and must not be deleted.**
            * `InvoiceFormModal` still renders it, for the push a SAVE performs —
            * which is now the only route a Xero outcome reaches a reader from
            * this screen at all.
            */}

          <div
            aria-busy={loading || undefined}
            className={cx(
              'transition-opacity duration-150 ease-out motion-reduce:transition-none',
              loading && result && 'opacity-60',
            )}
          >
            {/* Seven columns will not fit 320px, so rows become a stacked list.
                (Six from 2026-08-31, when the Xero column went, until From Xero
                arrived as a column of its own on 2026-09-02; seven before that.
                The count is worth keeping accurate — it is the reason the
                breakpoint exists, and the card list below has to carry every one
                of them.) */}
            <div className="md:hidden">
              {/*
                * A card has no header row, so all FOUR funnels — and with them
                * every sort control on this page — have nowhere to hang below
                * `md`. *SUPERSEDED 2026-09-02: "the Bill date funnel — and with
                * it the ONLY sort control on this page".* `FilterSheet` is their
                * mobile form, handed the SAME `headerFor` and `clearAll` the
                * table headers use, so there is one state store rather than two
                * that can disagree about how the list is ordered.
                *
                * ⚠️ **`headerFor` is the shared function, so the Status section
                * in here is the URL-bridged one too** — a status picked on a
                * phone writes `?statusId=` and is still applied after rotating to
                * a desktop width, and the "All statuses" Combobox above shows it.
                * Nothing extra was needed for that; it falls out of both surfaces
                * going through one `headerFor`, which is exactly why the bridge
                * lives there rather than at the header.
                */}
              {/* The bar owns the border and padding; FilterSheet's className
                  lands on its trigger Button, so styling a bar through it would
                  double the padding. */}
              <div className="border-b border-border px-4 py-2">
                {/*
                  * ✅ **`layout.orderedKeys`, NOT a hand-maintained list.**
                  * *SUPERSEDED: `INVOICE_COLUMN_KEYS`, a fourth copy of the table
                  * order whose own comment recorded it having already drifted —
                  * "TABLE ORDER, not declaration order, and the two now differ".*
                  * `FilterSheet` skips any key with no `filter`, so handing it
                  * every column is exactly the four sections it rendered before,
                  * and they now follow the reader's own arrangement.
                  *
                  * ⚠️ **`orderedKeys` INCLUDES HIDDEN COLUMNS, AND THAT IS THE
                  * POINT.** `visibleKeys` would take a hidden column's filter off
                  * this sheet — and a filter that is already APPLIED would then
                  * have no control anywhere to clear it from, which is the
                  * "a selected value with no option cannot be unticked" failure
                  * `withSelectedValues` exists to prevent, arriving through the
                  * column manager instead. Hiding a column is a statement about
                  * the desktop table, never about what may be filtered.
                  */}
                <FilterSheet
                  keys={layout.orderedKeys}
                  headerFor={headerFor}
                  activeCount={table.activeCount}
                  clearAll={table.clearAll}
                />
              </div>
              {initialLoad ? (
                <ListSkeleton />
              ) : visibleRows.length === 0 ? (
                emptyState
              ) : (
                <ul className="m-0 list-none p-0">
                  {visibleRows.map((invoice) => (
                    <li key={invoice.id} className="border-b border-border px-4 py-3 last:border-b-0">
                      <div className="flex items-start justify-between gap-3">
                        <SubjectCell invoice={invoice} />
                        {/* ⚠️ `ImportedFromXeroBadge` sat beside this until
                            2026-09-02, which is why the wrapper is gone with it —
                            a flex row around one badge. The fact it carried is
                            now the labelled "From Xero" line further down, so
                            the card and the desktop cell still say the same
                            thing in the same words. */}
                        <StatusBadge status={invoice.status} statuses={statuses.statuses} />
                      </div>
                      <div className="mt-2 flex items-center justify-between gap-3 text-xs">
                        {/* Always with its currency code: there is no exchange
                            rate in this system, so a bare number is an invitation
                            to add two currencies together. */}
                        <span className="text-fg-secondary">
                          <InvoiceMoney value={invoice.grandTotal} currency={invoice.currency} />
                        </span>
                        <span className="text-fg-muted">
                          <DueDateCell invoice={invoice} statuses={statuses.statuses} />
                        </span>
                      </div>
                      <p className="mt-1 m-0 text-xs text-fg-muted tabular">
                        Billed {formatDateOnly(invoice.billDate)}
                      </p>
                      {/*
                        * From Xero, on the card as well as in the cell — **a
                        * column that exists only above `md` is
                        * indistinguishable from one that was removed**, and this
                        * is the same rule the Projects badge below is here for.
                        *
                        * ⚠️ **LABELLED, unlike the desktop cell**, and that is
                        * the difference between a table and a card rather than
                        * an inconsistency: a bare "No" under a heading row means
                        * something and a bare "No" floating on a card means
                        * nothing. **A label is not helper text** — it names the
                        * value rather than explaining it, exactly as the visible
                        * From/To labels inside the bill-date range do.
                        *
                        * The imported-on date rides on `title` here too, so the
                        * two layouts disclose the same thing.
                        */}
                      <p
                        className="mt-1 m-0 text-xs text-fg-muted"
                        {...(invoice.importedFromXeroAt !== null
                          ? {
                              title: `Imported from Xero on ${formatDateTime(invoice.importedFromXeroAt)}`,
                            }
                          : {})}
                      >
                        From Xero{' '}
                        <span className="text-fg-secondary">
                          {importedFromXeroLabel(invoice.importedFromXeroAt)}
                        </span>
                      </p>
                      {/* Sent to client, on the card as well as in the cell —
                          labelled for the same reason From Xero is above. */}
                      {(() => {
                        const sentTitle = sentCellTitle(invoice);
                        return (
                          <p
                            className="mt-1 m-0 text-xs text-fg-muted"
                            {...(sentTitle !== null ? { title: sentTitle } : {})}
                          >
                            {SENT_TO_CLIENT_LABEL}{' '}
                            <span className="text-fg-secondary">{sentCellText(invoice)}</span>
                          </p>
                        );
                      })()}
                      {/* The Projects badge, on the card as well as in the cell.
                          The two layouts are the SAME list at two widths, and a
                          column that exists only on one is how a person on a
                          phone concludes a document has no state at all. */}
                      <div className="mt-1.5">
                        <LineProjectStatusCell status={invoice.lineProjectStatus} />
                      </div>
                      {/* The card's `XeroRowPushControl` — the same component the
                          desktop cell mounted, in `card` layout — stood here and
                          went with the column on 2026-08-31. Removed on BOTH
                          layouts in the one change: a control left on the card
                          alone is exactly the card/cell disagreement its shared
                          `variant` existed to prevent. */}
                      {/*
                        ⚠️ **THE "how many gates" NOTES ARE SUPERSEDED — there
                        is no gate on this bank at all any more.**
                        *SUPERSEDED: "Two independent gates now, not one: a
                        write role edits but may not delete, so the pair can
                        come apart", and after it "THREE gates now. Download
                        PDF is ungated by role but gated on the invoice being
                        in Xero, so the row has to appear for a `viewer` who
                        has one".*

                        Both were counting the conditions in the `&&` below,
                        which is gone. **Their REASON is untouched and is
                        restated in the note beneath**: the card and the
                        desktop cell must offer the same controls, and each of
                        those controls still gates itself independently.
                      */}
                      {/*
                        ⚠️ **NO LONGER CONDITIONAL, and the credit-note control
                        is what removed the condition.** It renders on every
                        row — enabled where the gate allows it, disabled with
                        its reason otherwise — so this bank always has at least
                        one control in it and the gate around it could never be
                        false. *SUPERSEDED: `{(canUpdate || canDelete ||
                        canDownloadPdf(invoice)) && (`.*

                        The rule the old condition served is unchanged and is
                        why the control is here at all: **a control offered on
                        the desktop cell and not on this card is
                        indistinguishable from one that was removed**, which is
                        the card/cell disagreement this file records having
                        already been made once.
                      */}
                      <div className="mt-2.5 flex flex-wrap items-center justify-end gap-2">
                          <DownloadPdfButton
                            kind="invoice"
                            document={invoice}
                            reference={invoice.subject}
                            variant="card"
                          />
                          <RaiseCreditNoteButton
                            invoice={invoice}
                            gate={creditNoteGateOf(
                              invoice,
                              canCreateCreditNote,
                              creditDenial,
                              creditStatuses.statuses,
                              creditStatuses.failed,
                            )}
                            variant="card"
                            busy={raiseCredit.checkingId === invoice.id}
                            onRaise={(row) => raiseCredit.start(row)}
                          />
                          {canUpdate && (
                            <Button
                              variant="secondary"
                              size="sm"
                              leadingIcon={<PencilIcon />}
                              onClick={() => setFormState({ invoice })}
                            >
                              Edit
                            </Button>
                          )}
                          {canDelete && (
                            <Button
                              variant="danger"
                              size="sm"
                              leadingIcon={<TrashIcon />}
                              onClick={() => void openDelete(invoice)}
                            >
                              Delete
                            </Button>
                          )}
                      </div>
                    </li>
                  ))}
                </ul>
              )}
            </div>

            <div className="hidden md:block">
              {/*
                * ✅ **ONE TABLE MOUNT, NOT TWO.** *SUPERSEDED: a `TableWrap` +
                * `Table` + `InvoicesTableHead` inside the loading branch and a
                * second, identical one inside the rows branch, each carrying a
                * comment telling the next reader to keep them "in step with" the
                * other.* They are now one subtree and only the `<TBody>` swaps.
                *
                * ⚠️ **IT IS ALSO WHAT KEEPS THE FROZEN OFFSETS HONEST.** The
                * header `<tr>` is the element `useFrozenOffsets` measures column
                * widths from and hangs a `ResizeObserver` on. Two branches meant
                * two different `<tr>` elements: the observer would have been left
                * watching a DETACHED row the moment the rows arrived, so every
                * frozen column would have kept the widths the SKELETON had —
                * silently, since a stale offset does not error, it just overlaps
                * two frozen columns by the difference.
                */}
              {!initialLoad && visibleRows.length === 0 ? (
                emptyState
              ) : (
                <TableWrap frozenHeader>
                  {/*
                    * 🔴 **THE FLOOR IS SUMMED FROM THE VISIBLE COLUMNS NOW, AND
                    * GOES THROUGH `style`.** *SUPERSEDED: `min-w-[1328px]`, twice,
                    * under a comment that walked a delta chain — "1080 + the
                    * `w-44` Xero column (176px) = 1256, − the `w-44` Owner column
                    * … = 1080, − the `w-44` Xero column itself … = 904, + the
                    * `w-36` Projects column (144px) = 1048, + the `w-32` From Xero
                    * column (128px) = 1176", then 1176 → 1328 — and then admitted
                    * in place: "**THE DELTA CHAIN ABOVE IS STALE BY 16px AND IS
                    * LEFT STANDING AS HISTORY: Projects went `w-36` → `w-40` on
                    * 2026-09-02 and THIS LITERAL WAS NOT MOVED WITH IT.**"*
                    *
                    * ⚠️ **A HAND-CARRIED FLOOR CANNOT SURVIVE HIDEABLE COLUMNS**,
                    * which is why it had to go rather than be corrected: the
                    * number now has to change every time a reader ticks a box.
                    * `tableMinWidth` sums `COLUMNS[key].width` over exactly the
                    * keys rendered, so hiding a column shrinks the table by that
                    * column and the arithmetic cannot drift from the header again.
                    * The default set sums to **1424** — 1120 of fixed columns
                    * plus the Invoice column's 304.
                    *
                    * ⚠️ **THAT FIGURE IS DERIVED AND THIS SENTENCE IS NOT, WHICH
                    * IS WHY IT HAS NOW BEEN WRONG TWICE.** *SUPERSEDED: "still
                    * sums to 1328 — 1024 of fixed columns", which was the sum
                    * when Actions declared 112 and was left 32 stale by the
                    * 2026-09-18 move to 144, then 64 more by the 2026-09-19
                    * re-base to 208.* Both times the declaration moved with a
                    * careful comment beside it and this one, three thousand
                    * lines away, was not in the diff. **Re-sum `COLUMNS`; never
                    * quote a total from here** — the code cannot go stale and
                    * the prose beside it always can.
                    *
                    * ✅ **THE `minWidth` IS ON THE `<Table>` NOW — IT TAKES A
                    * `style`.** *SUPERSEDED: a wrapper `<div>` carrying it,
                    * because "`Table` takes no `style` prop and is a shared
                    * primitive". That `<div>` also hosted the ref the header row
                    * was found through; `TR` takes a ref now, so both reasons for
                    * it are gone and it went with them.* The table is `w-full`
                    * and is itself the box that overflows `TableWrap`'s
                    * `overflow-x-auto`, at the same used width.
                    *
                    * ⚠️ **AND IT CANNOT BE A CLASS.** Tailwind v4 scans SOURCE
                    * TEXT, so `min-w-[${n}px]` emits no rule at all — the class
                    * lands in the DOM and does nothing, which reads as a table
                    * that simply chose not to hold its floor.
                    *
                    * ⚠️ **The Invoice column still carries no width of its own**,
                    * deliberately: it is the one FLUID column and takes whatever
                    * the fixed ones leave. Its contribution to this sum comes from
                    * `SubjectCell`'s content minimum instead — see `COLUMNS`.
                    */}
                  <Table style={{ minWidth: tableWidth }}>
                    <InvoicesTableHead
                      columns={renderedColumns}
                      headerFor={headerFor}
                      rowRef={headRowRef}
                    />
                    {initialLoad ? (
                      /* ⚠️ **`showActions` IS UNCONDITIONAL NOW.**
                         *SUPERSEDED, along with its own note — "The second
                         placeholder only when a second control is actually
                         coming — otherwise the skeleton is wider than the row
                         it stands in for": `showActions={canUpdate &&
                         canDelete}`.*

                         The credit-note control renders on every row whatever
                         the reader may do — disabled with its reason wherever
                         the gate refuses — so the actions cell is never
                         empty and a conditional placeholder would draw a
                         skeleton NARROWER than the rows replacing it, which
                         reads as the column gaining content on load. */
                      <TableSkeleton columns={renderedColumns} showActions />
                    ) : (
                      <TBody>
                        {visibleRows.map((invoice) => (
                          <TR key={invoice.id} interactive>
                            {/* The Xero cell stood in this row —
                                `XeroRowPushControl` in `cell` layout — and went
                                with its column on 2026-08-31. `/quotes` lost the
                                same cell in the same change, so the two lists
                                still do not drift. */}
                            {renderedColumns.map((column) => (
                              <TD
                                key={column.key}
                                numeric={COLUMNS[column.key].numeric}
                                {...bodyCellPropsOf(column.key, invoice)}
                                {...frozenPropsOf(column)}
                              >
                                <InvoiceRowCellContent
                                  columnKey={column.key}
                                  invoice={invoice}
                                  statuses={statuses.statuses}
                                  canUpdate={canUpdate}
                                  canDelete={canDelete}
                                  creditGate={creditNoteGateOf(
                                    invoice,
                                    canCreateCreditNote,
                                    creditDenial,
                                    creditStatuses.statuses,
                                    creditStatuses.failed,
                                  )}
                                  creditChecking={raiseCredit.checkingId === invoice.id}
                                  onEdit={(row) => setFormState({ invoice: row })}
                                  onDelete={(row) => void openDelete(row)}
                                  onRaiseCreditNote={(row) => raiseCredit.start(row)}
                                />
                              </TD>
                            ))}
                          </TR>
                        ))}
                      </TBody>
                    )}
                  </Table>
                </TableWrap>
              )}
            </div>
          </div>

          {/*
            * No totals row. Rows on one page can be in several currencies and
            * there is no exchange rate anywhere in the system, so any sum shown
            * here would either be wrong or need a per-currency breakdown nobody
            * has asked for yet. Deliberately left out.
            */}
          {result && (
            <ListPager
              page={page}
              totalPages={totalPages}
              total={total}
              firstRow={firstRow}
              lastRow={lastRow}
              perPage={PER_PAGE}
              showAll={showAll}
              loading={loading}
              truncated={showAll && showAllTruncated}
              noun="invoices"
              onPage={setPage}
              onToggleShowAll={(next) => {
                // Back to paging starts on page 1, never on whatever `page` held.
                if (!next) setPage(1);
                setShowAll(next);
              }}
            />
          )}
          </>
        )}
      </Panel>

      <InvoiceFormModal
        open={formState !== null || urlCreate}
        onClose={closeForm}
        invoice={formState?.invoice}
        // Only ever set for a create opened from the URL: an edit keeps its own
        // company, and the New invoice button keeps the picker.
        lockedCompany={lockedCompany}
        /*
         * /invoices returns no line count and there is no cheap way to get one
         * for a whole page of rows, so the totals are locked here rather than
         * guessed at: editing them on an invoice that has lines produces the
         * one state the design forbids. The detail page knows the count and
         * offers the inputs when it is genuinely zero.
         */
        hasLineItems="unknown"
        onSaved={handleSaved}
        /*
         * ⚠️ A SECOND, NARROWER SIGNAL — deliberately not `onSaved`.
         *
         * `onSaved` means "there is a record, reload and CLOSE", and several of
         * this modal's other mounts NAVIGATE on it. This one means only "the
         * line items moved"; the form stays open because the person is still
         * in it.
         */
        onLinesSaved={handleLinesSaved}
      />

      <ConfirmDialog
        open={pendingDelete !== null}
        onClose={() => {
          setPendingDelete(null);
          setDeleteError(null);
        }}
        onConfirm={() => void confirmDelete()}
        title={
          pendingDelete
            ? `Delete ${documentDisplayTitle(pendingDelete.subject, pendingDelete.xeroInvoiceNumber)}?`
            : 'Delete invoice?'
        }
        description={
          <>
            <p className="m-0">
              {confirmLineCount === null
                ? 'Any line items on this invoice are removed with it.'
                : confirmLineCount === 0
                  ? 'This invoice has no line items on it.'
                  : `This also removes the ${confirmLineCount} ${
                      confirmLineCount === 1 ? 'line item' : 'line items'
                    } on it.`}
            </p>
            {/* The delete never reaches Xero (backend `deleteInvoice`): the Xero invoice and its link survive. */}
            <p className="m-0 mt-2">
              If this invoice is in Xero, it stays there. Only the SEED EMERGE record is deleted,
              and that cannot be undone.
            </p>
          </>
        }
        confirmLabel="Delete invoice"
        loading={deleting}
        error={deleteError}
      />

      {/*
        🔴 **THE WHOLE RAISE-A-CREDIT-NOTE FLOW — the already-credited warning,
        the form with its line picker, and the partial report.** All three used
        to be written out here and again in `InvoiceDetailPage`;
        `useRaiseCreditNote` owns them now, so the two surfaces cannot word them
        differently or disagree about which lines a customer is credited for.

        ⚠️ **Mounted ONCE, at page level.** The triggers are one per row, in two
        layouts, and call `raiseCredit.start`; these dialogs sit outside them —
        which is also why the hook hands back an element rather than being a
        component this page could wrap a row in.
      */}
      {raiseCredit.dialogs}
    </>
  );
}

function InvoicesEmptyState({
  filtered,
  projectFiltered,
  onClear,
  hasError,
  action,
  denial,
}: {
  filtered: boolean;
  /** A project id went out on the list request — see the note below. */
  projectFiltered: boolean;
  onClear: () => void;
  hasError: boolean;
  action: ReactNode;
  /** Shown in place of the missing create button, when there is one to explain. */
  denial: string | null;
}) {
  if (hasError) {
    return (
      <EmptyState
        icon={<InvoiceIcon />}
        title="Nothing to show"
        description="The invoice list couldn’t be loaded. Check the message above and try again."
      />
    );
  }

  return (
    <EmptyState
      icon={<InvoiceIcon />}
      title={filtered ? 'No invoices match those filters' : 'No invoices yet'}
      description={
        /*
         * The project filter comes first because it is the one someone ARRIVES
         * on — the link off `/projects/:id` — rather than one they set here. An
         * empty result is the ordinary answer for it today: an invoice is put on
         * a project only when someone raises or edits it onto one, and a
         * converted quote carries its project across. That has to read as a fact
         * about the project rather than as a filter that failed.
         */
        projectFiltered
          ? 'Nothing has been billed against this project yet.'
          : filtered
            ? // A picklist id is never validated against the data: an unknown or
              // retired id returns an empty page rather than an error, so "no
              // matches" is not evidence that the filter itself is wrong.
              'Search matches the invoice subject only.'
            : 'Nothing billed yet.'
      }
      action={
        filtered ? (
          <Button variant="secondary" size="sm" onClick={onClear}>
            Clear filters
          </Button>
        ) : (
          /*
           * With nothing to list and no button to offer, say why rather than
           * showing a dead end. `denialReason` gives the true reason — "read-only
           * access", "does not have permission" — instead of the old
           * "administrators only", which was wrong for every write role.
           */
          (action ?? (denial ? <p className="m-0 text-xs text-fg-subtle">{denial}</p> : null))
        )
      }
    />
  );
}
